// ============================================================
//  CATEGORIES PAGE — ADMIN DASHBOARD
//  Path: src/pages/CategoriesPage.tsx
//
//  SAVE BUTTON (per category) — NOTHING SAVES UNTIL YOU PRESS SAVE.
//  Every edit on a category row (toggles, dropdowns, typed values, task
//  reach, vehicle requirement) now changes the row on screen only and is
//  held as "Unsaved". The Save button under the category name sends all of
//  it in ONE go, then re-reads the categories from the server (no "Loading…"
//  flash) so what is shown is what is stored. The status line says
//  "All changes saved", "● Unsaved: …", "Saving…", "✓ Saved 2:31 PM" or the
//  error; a leave-the-page warning shows while anything is unsaved, and a
//  failed save keeps the edits so it can be retried.
//
//  Previously toggles saved instantly while typed values saved only when the
//  cursor left the box, and choosing Percent / Flat $ for the service fee
//  saved nothing until a number was typed — rows looked changed and reverted
//  after a refresh.
//
//  NOT held by this button (each keeps its own save): the Insurance Defaults
//  panel, Platform Defaults, the insurance fee row editor, and licence
//  requirements.
//
//  Manages category flags, fees, and location options.
//  Communication settings (voice/video/chat per stage) have
//  moved to CategoryCommunicationPage — linked from the header
//  and the sidebar. This page links there rather than duplicating
//  controls that don't fit in a table column.
//
//  CHANGE: Insurance section now uses the fee split model.
//  The old flat insuranceFeeType / insuranceFeeValue single-row
//  fields have been replaced by CategoryInsuranceFee rows — one
//  per insurance type (liability, cargo, commercial_auto,
//  professional_indemnity, general), each with its own rate,
//  customer/tasker split, and waiver rule. The insuranceFeeExempt
//  flag remains as a quick "no insurance at all" kill-switch.
//
//  CHANGE: Service and processing fee sections now expose
//  customer/tasker split percentage controls (serviceFeeCustomerPct /
//  serviceFeeTaskerPct, processingFeeCustomerPct /
//  processingFeeTaskerPct). The default for both is 100% customer /
//  0% tasker (unchanged from the previous implicit behaviour).
//
//  CHANGE: insurance — all five insurance types are always listed for
//  every category. A type with no fee row shows "0% — not charged" (no
//  row = nothing is charged, which is exactly what the backend does), so
//  every type starts at 0 and the admin only types a rate where one is
//  wanted. "Edit" on a type opens the editor pre-filled with that type's
//  current values (or 0 for a type not set yet); choosing another type in
//  the editor loads that type's values instead of keeping the previous
//  one's, so a save can't copy one type's rate onto another.
//
//  CHANGE: task reach — Radius is the default. A category with no reach
//  saved shows Radius, and switching a category to Radius starts at
//  100 km, or 30 km for the Food Delivery & Meal Services group (was
//  15 km for everything). The same defaults live in the backend
//  (schema default + seed.js), which is what actually applies them to
//  existing and new categories.
//
//  Access:
//    super_admin, admin  → full access
//    customer_service    → view only
// ============================================================

import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  listAllCategoriesAdmin,
  updateCategory as updateCategoryNow,
  updateCategoryPickupFlags as updateCategoryPickupFlagsNow,
  getCategoryGroupOrder,
  upsertCategoryInsuranceFee,
  deleteCategoryInsuranceFee,
  listCategoryLicenseRequirements,
  createCategoryLicenseRequirement,
  updateCategoryLicenseRequirement,
  deleteCategoryLicenseRequirement,
  getPlatformFeeSettings,
  updatePlatformFeeSettings,
} from '../services/admin.service'
import { useAuth } from '../context/AuthContext'
import InsuranceDefaultsPanel from './InsuranceDefaultsPanel'
import type { CategoryInsuranceFee, InsuranceType, FeeType, CategoryLicenseRequirement, LicenseDocType, LicenseVerificationLevel } from '../types'

interface Category {
  id:              string
  name:            string
  slug:            string
  group:           string | null
  isInHome:        boolean
  isActive:        boolean
  requiresLicense: boolean
  requiresBackgroundCheck: boolean
  requiresVulnerableSectorCheck: boolean
  commissionExempt: boolean
  allowsZeroPrice:  boolean
  commissionPercent: number | null
  serviceFeeExempt:        boolean
  serviceFeeType:          'percent' | 'flat' | null
  serviceFeeValue:         number | null
  // Split percentages — must always sum to 100
  serviceFeeCustomerPct:   number
  serviceFeeTaskerPct:     number
  processingFeeExempt:     boolean
  processingFeeType:       'percent' | 'flat' | null
  processingFeeValue:      number | null
  processingFeeCustomerPct: number
  processingFeeTaskerPct:   number
  callTimeLimitMinutes: number | null
  allowsCustomerAddress:   boolean
  allowsExpertLocation:    boolean
  allowsVirtual:           boolean
  isTwoLocation:      boolean
  requiresPickupCode: boolean
  vehicleRequirement: 'any' | 'motorized' | 'car_van' | 'van_truck'
  allowsHourlyPricing: boolean
  allowsFlatPricing:   boolean
  allowsFreeToConnect: boolean
  // Tax type decides the tasker's job-price tax; paymentChannel EXTERNAL = paid outside the platform (all amounts 0).
  taxType:        'STANDARD' | 'EXEMPT' | 'ZERO_RATED' | 'MANDATORY_REGISTRATION' | 'TRANSACTION_DEPENDENT'
  paymentChannel: 'INTERNAL' | 'EXTERNAL'
  // Insurance — quick kill-switch. Per-type rows live in insuranceFeeRows.
  insuranceFeeExempt: boolean
  // Days a job's insurance covers it, counted from the scheduled day (1 = that day only)
  jobSpanDays?: number
  // 'manual' = the fee rows below; 'insurer' = live price from the connected insurer (falls back to the rows)
  insurancePricing?: 'manual' | 'insurer'
  // Per-type insurance fee rows (CategoryInsuranceFee model)
  insuranceFeeRows:   CategoryInsuranceFee[]
  // Individual-expert liability insurance requirement — separate
  // from the company-only requirement (unconditional for any
  // expertType === 'company' account, regardless of category).
  // When true, an INDIVIDUAL expert who selects this category must
  // also upload a liability_insurance document before submitting
  // for review. minLiabilityCoverageAmount is informational-only
  // guidance shown to the expert (e.g. "$2,000,000 minimum") — not
  // a machine-enforced amount.
  requiresLiabilityInsurance: boolean
  minLiabilityCoverageAmount: number | null
  // Insurance lines an admin set to MANDATORY for individuals (default is
  // Optional). From the server's category list — see the warning badge.
  mandatoryIndividualInsurance?: { id: string; label: string; insuranceType: string | null }[]
  // ── Task reach + availability count + escalation ────────────
  // All admin-controlled. Protected by adminOverriddenFields —
  // changes here are never silently reverted by a redeploy.
  taskReach:                  'local' | 'radius' | 'global'
  maxTaskRadiusKm:            number | null
  allowCustomerToExpandReach: boolean
  showAvailabilityCount:      'none' | 'total' | 'online'
  escalationEnabled:          boolean
  escalationStep1Minutes:     number | null
  escalationStep2Minutes:     number | null
  escalationStep3Minutes:     number | null
}

const INSURANCE_TYPES: InsuranceType[] = [
  'liability', 'cargo', 'commercial_auto', 'professional_indemnity',
]

const INSURANCE_TYPE_LABELS: Record<InsuranceType, string> = {
  liability:              'Liability',
  cargo:                  'Cargo',
  commercial_auto:        'Commercial Auto',
  professional_indemnity: 'Professional Indemnity',
}

const LICENSE_DOC_TYPE_LABELS: Record<LicenseDocType, string> = {
  trade_license:                   'Trade Licence',
  gas_fitter_certificate:          'Gas Fitter Certificate',
  electrical_license:              'Electrical Licence',
  food_handler_certificate:        'Food Handler Certificate',
  financial_services_license:      'Financial Services Licence',
  notary_commission:               'Notary Commission',
  immigration_consultant_license:  'Immigration Consultant Licence',
  drone_pilot_certificate:         'Drone Pilot Certificate',
  wsib_certificate:                'WSIB Certificate',
  insurance_proof:                 'Insurance Proof',
  real_estate_license:             'Real Estate Licence',
  mortgage_broker_license:         'Mortgage Broker Licence',
  law_society_license:             'Law Society Licence',
  appraisal_license:               'Appraisal Licence',
  survey_license:                  'Survey Licence',
  securities_license:              'Securities Licence',
  travel_registration:             'Travel Registration',
  insurance_broker_license:        'Insurance Broker Licence',
  affiliation_proof:               'Affiliation Proof',
  other:                           'Other',
}
const LICENSE_DOC_TYPES = Object.keys(LICENSE_DOC_TYPE_LABELS) as LicenseDocType[]

// 'individual' (default): the expert must personally hold and upload
// proof. 'company': satisfied for a roster worker by their COMPANY's
// own verified document instead — the worker is never asked to
// upload their own copy (e.g. Travel Services' TICO registration,
// held at the agency level under Ontario law). See
// schema.prisma's LicenseVerificationLevel / admin.service.js's
// verifyDocument (backend cascade) for the full rationale.
const VERIFICATION_LEVEL_LABELS: Record<LicenseVerificationLevel, string> = {
  individual: 'Individual — expert uploads',
  company:    'Company — inherited from roster company',
}

const sortGroupNames = (groups: string[], order: string[]): string[] => {
  return [...groups].sort((a, b) => {
    const ai = order.indexOf(a)
    const bi = order.indexOf(b)
    if (ai === -1 && bi === -1) return a.localeCompare(b)
    if (ai === -1) return 1
    if (bi === -1) return -1
    return ai - bi
  })
}

// Enforces customerPct + taskerPct === 100.
// When one side is changed, the other auto-adjusts.
const clampPct = (v: number): number => Math.max(0, Math.min(100, Math.round(v)))

// Plain-words names for the "Unsaved: …" status line, by stored field.
const STAGED_LABELS: Record<string, string> = {
  isActive: 'Active',
  allowsCustomerAddress: 'Location', allowsExpertLocation: 'Location', allowsVirtual: 'Location',
  requiresBackgroundCheck: 'Background check',
  requiresVulnerableSectorCheck: 'Vulnerable sector check',
  isTwoLocation: 'Two-location', requiresPickupCode: 'Pickup code',
  allowsHourlyPricing: 'Pricing', allowsFlatPricing: 'Pricing', allowsFreeToConnect: 'Pricing',
  allowsZeroPrice: 'Zero price',
  commissionExempt: 'Commission', commissionPercent: 'Commission %',
  serviceFeeExempt: 'Service fee', serviceFeeType: 'Service fee', serviceFeeValue: 'Service fee',
  serviceFeeCustomerPct: 'Service fee split', serviceFeeTaskerPct: 'Service fee split',
  processingFeeCustomerPct: 'Processing split', processingFeeTaskerPct: 'Processing split',
  taxType: 'Tax type', paymentChannel: 'Payment channel',
  insuranceFeeExempt: 'Insurance', insurancePricing: 'Insurance', jobSpanDays: 'Insurance coverage days',
  requiresLiabilityInsurance: 'Liability insurance', minLiabilityCoverageAmount: 'Minimum coverage',
  vehicleRequirement: 'Vehicle requirement',
  taskReach: 'Task reach', maxTaskRadiusKm: 'Task reach', showAvailabilityCount: 'Availability count',
  allowCustomerToExpandReach: 'Task reach', escalationEnabled: 'Escalation',
  escalationStep1Minutes: 'Escalation', escalationStep2Minutes: 'Escalation', escalationStep3Minutes: 'Escalation',
}
// These two go through their own endpoint (dependency rules between them).
const PICKUP_FLAG_KEYS = ['isTwoLocation', 'requiresPickupCode']

// The reach settings save instantly. A failed save used to silently snap the
// control back to its old value (e.g. choosing "Radius" flipped back to
// "Local") with no hint why. Now the admin is told what went wrong.
const reachSaveError = (err: any) => {
  const message = err?.response?.data?.message ?? err?.message ?? 'Unknown error'
  window.alert(`Could not save this setting.\n\n${message}`)
}

// Default notification radius: 100 km, or 30 km for the Food Delivery
// & Meal Services group. Must match the backend's seed.js.
const DEFAULT_RADIUS_KM      = 100
const DEFAULT_FOOD_RADIUS_KM = 30
const FOOD_DELIVERY_GROUP    = 'Food Delivery & Meal Services'
const defaultRadiusFor = (c: { group?: string | null }) =>
  (c.group === FOOD_DELIVERY_GROUP ? DEFAULT_FOOD_RADIUS_KM : DEFAULT_RADIUS_KM)

const CategoriesPage: React.FC = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const canModify = user?.roles.includes('super_admin') || user?.roles.includes('admin')

  const [categories, setCategories] = useState<Category[]>([])
  const [groupOrder, setGroupOrder] = useState<string[]>([])
  const [loading, setLoading]       = useState(true)
  const [search, setSearch]         = useState('')
  const [savingId, setSavingId]     = useState<string | null>(null)
  const [saveError, setSaveError]   = useState<string | null>(null)

  // ── Tabs ────────────────────────────────────────────────────
  // NEW: the table used to show every column at once — Flags,
  // Location, BG Check, Two-Loc, Pickup Code, Expert Rate, Pricing,
  // Service Fee, Processing Split, Insurance (which itself buried
  // the insurance fee rows, the individual-liability toggle, the
  // licence requirements checklist button, AND the reach/escalation
  // button all in one column) and Comm — 13 columns wide, effectively
  // unreadable once there are more than a handful of categories in a
  // group. Splitting into tabs groups related settings together and
  // only renders the columns for whichever tab is active. Category
  // name and Active status stay visible in every tab (they're the
  // "which row am I looking at" anchor); Comm stays visible too since
  // it's just a one-click link to a different page, not a control.
  // No existing handler, state, or save logic changes — this only
  // changes which already-built <td>s are shown at once.
  const [activeTab, setActiveTab] = useState<'basics' | 'licensing' | 'reach' | 'payment'>('basics')

  // ── Platform-wide fee defaults ───────────────────────────────
  // The fallback every category's blank commission/service/processing
  // fee field in the Basics tab below actually resolves to — see
  // PlatformFeeSettings in schema.prisma and platformFeeOverrides.js.
  // Shown as a small panel at the top of Basics rather than a
  // separate page: an admin editing a category's override is looking
  // at exactly this screen when they need to know what the default is.
  // Loaded lazily the first time Basics is opened, not on mount —
  // licensing/reach never need it.
  type PlatformFeeField = { override: number | string | null; staticDefault: number | string | null; effective: number | string | null }
  type PlatformFeeSettingsShape = {
    platformFeePercent: PlatformFeeField; insuranceFeePercent: PlatformFeeField
    serviceFeeType: PlatformFeeField; serviceFeeValue: PlatformFeeField
    processingFeePercent: PlatformFeeField; processingFeeFixed: PlatformFeeField
    taxRatePercent: PlatformFeeField
    updatedAt: string | null; updatedByUid: string | null
  }
  type PlatformFeeKey = keyof Omit<PlatformFeeSettingsShape, 'updatedAt' | 'updatedByUid'>
  const [platformSettings, setPlatformSettings] = useState<PlatformFeeSettingsShape | null>(null)
  const [platformSettingsLoaded, setPlatformSettingsLoaded] = useState(false)
  const [platformDraft, setPlatformDraft] = useState<Record<PlatformFeeKey, string>>({
    platformFeePercent: '', insuranceFeePercent: '', serviceFeeType: '',
    serviceFeeValue: '', processingFeePercent: '', processingFeeFixed: '', taxRatePercent: '',
  })
  const [platformSaving, setPlatformSaving]         = useState(false)
  const [platformError, setPlatformError]           = useState('')
  const [platformSaveMessage, setPlatformSaveMessage] = useState('')
  const [platformPanelOpen, setPlatformPanelOpen]   = useState(false)

  const NUMERIC_PLATFORM_FIELDS: { key: PlatformFeeKey; label: string; suffix: string; step: string }[] = [
    { key: 'platformFeePercent',   label: 'Platform Commission',         suffix: '%', step: '0.1' },
    { key: 'serviceFeeValue',      label: 'Service Fee Amount',          suffix: '',  step: '0.01' },
    { key: 'processingFeePercent', label: 'Stripe Processing Fee %',     suffix: '%', step: '0.01' },
    { key: 'processingFeeFixed',   label: 'Stripe Processing Fee Fixed', suffix: '$', step: '0.01' },
    { key: 'insuranceFeePercent',  label: 'Legacy Insurance Fee %',      suffix: '%', step: '0.1' },
    { key: 'taxRatePercent',       label: 'Tax Rate',                    suffix: '%', step: '0.01' },
  ]

  const draftFromPlatformSettings = (s: PlatformFeeSettingsShape) => ({
    platformFeePercent:   s.platformFeePercent.override   != null ? String(s.platformFeePercent.override)   : '',
    insuranceFeePercent:  s.insuranceFeePercent.override   != null ? String(s.insuranceFeePercent.override)  : '',
    serviceFeeType:       s.serviceFeeType.override        != null ? String(s.serviceFeeType.override)       : '',
    serviceFeeValue:      s.serviceFeeValue.override       != null ? String(s.serviceFeeValue.override)      : '',
    processingFeePercent: s.processingFeePercent.override  != null ? String(s.processingFeePercent.override) : '',
    processingFeeFixed:   s.processingFeeFixed.override    != null ? String(s.processingFeeFixed.override)   : '',
    taxRatePercent:       s.taxRatePercent.override         != null ? String(s.taxRatePercent.override)      : '',
  })

  const fetchPlatformSettings = async () => {
    setPlatformError('')
    try {
      const res = await getPlatformFeeSettings()
      const s: PlatformFeeSettingsShape = res.data.data
      setPlatformSettings(s)
      setPlatformDraft(draftFromPlatformSettings(s))
    } catch (err: any) {
      console.error('Failed to fetch platform fee settings:', err)
      setPlatformError(err?.response?.data?.message || 'Failed to load platform fee defaults.')
    } finally {
      setPlatformSettingsLoaded(true)
    }
  }

  // Labels for the "Default" option in each category's own Rate /
  // Service Fee Type dropdown below — shows the actual live platform
  // number (e.g. "Default (5%)") instead of a bare, unexplained
  // "Default", so it's clear at a glance what picking Default on a
  // category actually resolves to right now. Falls back to a plain
  // "Default" while platformSettings hasn't loaded yet (or failed to).
  const platformCommissionDefaultLabel = () => {
    const pct = platformSettings?.platformFeePercent.effective
    return pct != null ? `Default (${pct}%)` : 'Default'
  }
  const platformServiceFeeDefaultLabel = () => {
    const type  = platformSettings?.serviceFeeType.effective
    const value = platformSettings?.serviceFeeValue.effective
    if (value == null) return 'Default'
    return type === 'flat' ? `Default ($${value})` : `Default (${value}%)`
  }

  // fetchPlatformSettings is intentionally omitted from the deps array —
  // it's redefined on every render (not wrapped in useCallback), so
  // including it would re-run this effect every render instead of just
  // once per Basics-tab visit. platformSettingsLoaded is what actually
  // gates it from firing more than once.
  useEffect(() => {
    if (activeTab === 'basics' && !platformSettingsLoaded) fetchPlatformSettings()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, platformSettingsLoaded])

  const handleSavePlatformSettings = async () => {
    if (!platformSettings) return
    setPlatformSaving(true)
    setPlatformError('')
    setPlatformSaveMessage('')
    try {
      const payload: Record<string, number | string | null> = {}
      for (const field of NUMERIC_PLATFORM_FIELDS) {
        const raw = platformDraft[field.key].trim()
        const current = platformSettings[field.key].override
        if (raw === '') {
          if (current != null) payload[field.key] = null
        } else {
          const n = Number(raw)
          if (!Number.isFinite(n)) {
            setPlatformError(`${field.label} must be a number`)
            setPlatformSaving(false)
            return
          }
          if (String(current ?? '') !== raw) payload[field.key] = n
        }
      }
      const rawType = platformDraft.serviceFeeType.trim()
      const currentType = platformSettings.serviceFeeType.override
      if (rawType === '') {
        if (currentType != null) payload.serviceFeeType = null
      } else if (rawType !== currentType) {
        payload.serviceFeeType = rawType
      }

      if (Object.keys(payload).length === 0) {
        setPlatformSaveMessage('Nothing changed.')
        setPlatformSaving(false)
        return
      }

      const res = await updatePlatformFeeSettings(payload)
      const s: PlatformFeeSettingsShape = res.data.data
      setPlatformSettings(s)
      setPlatformDraft(draftFromPlatformSettings(s))
      setPlatformSaveMessage('Saved — takes effect within about a minute.')
    } catch (err: any) {
      console.error('Failed to update platform fee settings:', err)
      setPlatformError(err?.response?.data?.message || 'Failed to save platform fee defaults.')
    } finally {
      setPlatformSaving(false)
    }
  }

  // Tracks which category's insurance section is expanded (row management)
  const [expandedInsuranceId, setExpandedInsuranceId] = useState<string | null>(null)
  // Tracks which category's reach/escalation section is expanded
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [expandedReachId, setExpandedReachId] = useState<string | null>(null)
  // Tracks in-progress new-row form state per category
  const [newRowDraft, setNewRowDraft] = useState<Record<string, {
    insuranceType: InsuranceType; feeType: FeeType; feeValue: string;
    customerPct: string; taskerPct: string; waivedIfVerified: boolean
  }>>({})

  // ── Licence requirement checklist management ────────────────
  // Separate from CategoryInsuranceFee above: this is the checklist
  // of documents an expert must provide (CategoryLicenseRequirement),
  // and insuranceType here is what the backend's verifyDocument
  // reads to resolve which InsuranceType a requirement proves — the
  // fix for requirements sharing docType: 'insurance_proof' across
  // liability / commercial auto / professional indemnity with no
  // other structured way to tell them apart. Fetched lazily, per
  // category, only once that category's requirements panel is
  // expanded — there's no reason to load every category's checklist
  // up front.
  const [expandedRequirementsId, setExpandedRequirementsId] = useState<string | null>(null)
  const [requirementsByCategory, setRequirementsByCategory] = useState<Record<string, CategoryLicenseRequirement[]>>({})
  const [requirementsLoading, setRequirementsLoading] = useState<Record<string, boolean>>({})
  const [newRequirementDraft, setNewRequirementDraft] = useState<Record<string, {
    docType: LicenseDocType; label: string; description: string; insuranceType: InsuranceType | ''
    verificationLevel: LicenseVerificationLevel
    isOptional: boolean
  }>>({})
  const [editingRequirementId, setEditingRequirementId] = useState<string | null>(null)
  const [editRequirementDraft, setEditRequirementDraft] = useState<{
    docType: LicenseDocType; label: string; description: string; insuranceType: InsuranceType | ''
    verificationLevel: LicenseVerificationLevel
    isOptional: boolean
  } | null>(null)
  const [requirementSavingId, setRequirementSavingId] = useState<string | null>(null)
  const [requirementError, setRequirementError] = useState<string | null>(null)

  // ── Held (unsaved) edits ────────────────────────────────────
  // Every edit handler below used to call the API straight away. They still
  // call updateCategory / updateCategoryPickupFlags / updateCategoryReach —
  // but those three are now THESE local functions, which only hold the
  // change. The Save button (handleSaveCategory) is what sends it.
  const [stagedEdits, setStagedEdits] = useState<Record<string, Record<string, any>>>({})
  const stagedRef = useRef<Record<string, Record<string, any>>>({})
  stagedRef.current = stagedEdits

  const stageEdit = (categoryId: string, fields: Record<string, any>) => {
    setStagedEdits((prev) => ({ ...prev, [categoryId]: { ...prev[categoryId], ...fields } }))
  }
  const updateCategory = async (categoryId: string, fields: Record<string, any>) => { stageEdit(categoryId, fields) }
  const updateCategoryPickupFlags = async (categoryId: string, fields: Record<string, any>) => { stageEdit(categoryId, fields) }
  const updateCategoryReach = async (categoryId: string, fields: Record<string, any>) => { stageEdit(categoryId, fields) }

  // A re-read from the server must not wipe edits that are still waiting for
  // Save — lay them back on top of what the server returned.
  const withStaged = (list: Category[]): Category[] => list.map((c) => {
    const staged = stagedRef.current[c.id]
    if (!staged) return c
    const merged: any = { ...c, ...staged }
    if (staged.isTwoLocation === false) merged.requiresPickupCode = false
    if (staged.requiresPickupCode === true) merged.isTwoLocation = true
    return merged
  })

  const fetchCategories = async () => {
    setLoading(true)
    try {
      const res = await listAllCategoriesAdmin()
      setCategories(withStaged(res.data.data))
    } catch (err: any) {
      setSaveError(err.response?.data?.message || 'Failed to load categories')
    } finally {
      setLoading(false)
    }
  }

  const fetchGroupOrder = async () => {
    try {
      const res = await getCategoryGroupOrder()
      setGroupOrder(res.data.data)
    } catch (err) {
      // Falls back to alphabetical sort — non-fatal, but still worth
      // logging so a persistent failure here is visible somewhere.
      console.error('Failed to fetch category group order:', err)
    }
  }

  // Runs once on mount, on purpose.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchCategories(); fetchGroupOrder() }, [])

  // ── Location flags ──────────────────────────────────────────
  const handleLocationFlagToggle = async (category: Category, flag: 'allowsCustomerAddress' | 'allowsExpertLocation' | 'allowsVirtual') => {
    if (!canModify) return
    const next = {
      allowsCustomerAddress: category.allowsCustomerAddress,
      allowsExpertLocation:  category.allowsExpertLocation,
      allowsVirtual:         category.allowsVirtual,
      [flag]: !category[flag],
    }
    if (!next.allowsCustomerAddress && !next.allowsExpertLocation && !next.allowsVirtual) {
      setSaveError('At least one location option must stay enabled — customers need somewhere for the task to happen.')
      return
    }
    setSavingId(category.id)
    setSaveError(null)
    setCategories((prev) => prev.map((c) => (c.id === category.id ? { ...c, ...next } : c)))
    try {
      await updateCategory(category.id, next)
    } catch (err: any) {
      setSaveError(err.response?.data?.message || 'Failed to save — reverting')
      fetchCategories()
    } finally {
      setSavingId(null)
    }
  }

  // ── Background check ────────────────────────────────────────
  const handleBackgroundCheckToggle = async (categoryId: string, requiresBackgroundCheck: boolean) => {
    if (!canModify) return
    setSavingId(categoryId)
    setSaveError(null)
    setCategories((prev) => prev.map((c) => (c.id === categoryId ? { ...c, requiresBackgroundCheck } : c)))
    try {
      await updateCategory(categoryId, { requiresBackgroundCheck })
    } catch (err: any) {
      setSaveError(err.response?.data?.message || 'Failed to save — reverting')
      fetchCategories()
    } finally {
      setSavingId(null)
    }
  }

  // ── Vulnerable Sector Check ─────────────────────────────────
  // A category that needs the VSC makes the tasker upload that check in
  // the Background check step instead of the standard one. Turning it on
  // also turns the standard background check off (one check, not two);
  // both stay individually editable afterwards.
  const handleVulnerableSectorToggle = async (categoryId: string, requiresVulnerableSectorCheck: boolean) => {
    if (!canModify) return
    setSavingId(categoryId)
    setSaveError(null)
    const payload: { requiresVulnerableSectorCheck: boolean; requiresBackgroundCheck?: boolean } = { requiresVulnerableSectorCheck }
    if (requiresVulnerableSectorCheck) payload.requiresBackgroundCheck = false
    setCategories((prev) => prev.map((c) => (c.id === categoryId ? { ...c, ...payload } : c)))
    try {
      await updateCategory(categoryId, payload)
    } catch (err: any) {
      setSaveError(err.response?.data?.message || 'Failed to save — reverting')
      fetchCategories()
    } finally {
      setSavingId(null)
    }
  }

  // ── Pricing toggles ─────────────────────────────────────────
  const handlePricingToggle = async (categoryId: string, field: 'allowsHourlyPricing' | 'allowsFlatPricing' | 'allowsFreeToConnect', value: boolean) => {
    if (!canModify) return
    const category = categories.find((c) => c.id === categoryId)
    if (!value) {
      const remaining = ['allowsHourlyPricing', 'allowsFlatPricing', 'allowsFreeToConnect']
        .filter((f) => f !== field)
        .some((f) => category?.[f as keyof typeof category])
      if (!remaining) { setSaveError('At least one pricing option must remain enabled.'); return }
    }
    setSavingId(categoryId)
    setSaveError(null)
    setCategories((prev) => prev.map((c) => c.id === categoryId ? { ...c, [field]: value } : c))
    try {
      await updateCategory(categoryId, { [field]: value })
    } catch (err: any) {
      setSaveError(err.response?.data?.message || 'Failed to save — reverting')
      fetchCategories()
    } finally {
      setSavingId(null)
    }
  }

  // ── Tax type / payment channel ──────────────────────────────
  const handleTaxChannelChange = async (categoryId: string, field: 'taxType' | 'paymentChannel', value: string) => {
    if (!canModify) return
    if (field === 'paymentChannel') {
      const name = categories.find((c) => c.id === categoryId)?.name ?? 'this category'
      const msg = value === 'EXTERNAL'
        ? `Make "${name}" External? New bookings will be free consultations: $0, no card charge, no payout. Taskers who only have External categories will no longer need a bank account.`
        : `Make "${name}" Internal? New bookings will be charged through the app, and taskers in this category will need a bank account.`
      if (!window.confirm(msg)) return
    }
    setSavingId(categoryId)
    setSaveError(null)
    setCategories((prev) => prev.map((c) => c.id === categoryId ? { ...c, [field]: value } : c))
    try {
      await updateCategory(categoryId, { [field]: value })
    } catch (err: any) {
      setSaveError(err.response?.data?.message || 'Failed to save — reverting')
      fetchCategories()
    } finally {
      setSavingId(null)
    }
  }

  // ── Active toggle ───────────────────────────────────────────
  const handleActiveToggle = async (categoryId: string, isActive: boolean) => {
    if (!canModify) return
    setSavingId(categoryId)
    setSaveError(null)
    setCategories((prev) => prev.map((c) => (c.id === categoryId ? { ...c, isActive } : c)))
    try {
      await updateCategory(categoryId, { isActive })
    } catch (err: any) {
      setSaveError(err.response?.data?.message || 'Failed to save — reverting')
      fetchCategories()
    } finally {
      setSavingId(null)
    }
  }

  // ── Commission ──────────────────────────────────────────────
  const handleCommissionExemptToggle = async (categoryId: string, commissionExempt: boolean) => {
    if (!canModify) return
    setSavingId(categoryId)
    setSaveError(null)
    setCategories((prev) => prev.map((c) => (c.id === categoryId ? { ...c, commissionExempt } : c)))
    try {
      await updateCategory(categoryId, { commissionExempt })
    } catch (err: any) {
      setSaveError(err.response?.data?.message || 'Failed to save — reverting')
      fetchCategories()
    } finally {
      setSavingId(null)
    }
  }

  const handleAllowsZeroPriceToggle = async (categoryId: string, allowsZeroPrice: boolean) => {
    if (!canModify) return
    setSavingId(categoryId)
    setSaveError(null)
    setCategories((prev) => prev.map((c) => (c.id === categoryId ? { ...c, allowsZeroPrice } : c)))
    try {
      await updateCategory(categoryId, { allowsZeroPrice })
    } catch (err: any) {
      setSaveError(err.response?.data?.message || 'Failed to save — reverting')
      fetchCategories()
    } finally {
      setSavingId(null)
    }
  }

  const [commissionPercentDrafts, setCommissionPercentDrafts] = useState<Record<string, string>>({})
  // Mirrors commissionPercentDrafts above — local text while the
  // admin is typing, committed onBlur via handleMinLiabilityCoverageBlur.
  const [liabilityCoverageDrafts, setLiabilityCoverageDrafts] = useState<Record<string, string>>({})
  const [customRateModeIds, setCustomRateModeIds] = useState<Set<string>>(new Set())
  const PRESET_RATES = [0, 5, 10, 15, 20, 25]

  const handleCommissionPercentBlur = async (category: Category) => {
    if (!canModify) return
    const draft = commissionPercentDrafts[category.id]
    if (draft === undefined) return
    const trimmed = draft.trim()
    const newValue = trimmed === '' ? null : Number(trimmed)
    if (newValue !== null && (!Number.isFinite(newValue) || newValue < 0 || newValue > 100)) {
      setSaveError('Commission percent must be a number between 0 and 100, or blank for the platform default.')
      setCommissionPercentDrafts((prev) => { const next = { ...prev }; delete next[category.id]; return next })
      return
    }
    if (newValue === category.commissionPercent) {
      setCommissionPercentDrafts((prev) => { const next = { ...prev }; delete next[category.id]; return next })
      return
    }
    setSavingId(category.id)
    setSaveError(null)
    setCategories((prev) => prev.map((c) => (c.id === category.id ? { ...c, commissionPercent: newValue } : c)))
    setCommissionPercentDrafts((prev) => { const next = { ...prev }; delete next[category.id]; return next })
    try {
      await updateCategory(category.id, { commissionPercent: newValue })
    } catch (err: any) {
      setSaveError(err.response?.data?.message || 'Failed to save — reverting')
      fetchCategories()
    } finally {
      setSavingId(null)
    }
  }

  const handleCommissionRateSelect = async (category: Category, selection: string) => {
    if (!canModify) return
    if (selection === 'custom') {
      setCustomRateModeIds((prev) => new Set(prev).add(category.id))
      return
    }
    setCustomRateModeIds((prev) => { const next = new Set(prev); next.delete(category.id); return next })
    setCommissionPercentDrafts((prev) => { const next = { ...prev }; delete next[category.id]; return next })
    const newValue = selection === 'default' ? null : Number(selection)
    if (newValue === category.commissionPercent) return
    setSavingId(category.id)
    setSaveError(null)
    setCategories((prev) => prev.map((c) => (c.id === category.id ? { ...c, commissionPercent: newValue } : c)))
    try {
      await updateCategory(category.id, { commissionPercent: newValue })
    } catch (err: any) {
      setSaveError(err.response?.data?.message || 'Failed to save — reverting')
      fetchCategories()
    } finally {
      setSavingId(null)
    }
  }

  const commissionRateSelection = (category: Category): string => {
    if (customRateModeIds.has(category.id)) return 'custom'
    if (category.commissionPercent === null) return 'default'
    if (PRESET_RATES.includes(category.commissionPercent)) return String(category.commissionPercent)
    return 'custom'
  }

  // ── Service fee ─────────────────────────────────────────────
  const handleServiceFeeExemptToggle = async (categoryId: string, serviceFeeExempt: boolean) => {
    if (!canModify) return
    setSavingId(categoryId)
    setSaveError(null)
    setCategories((prev) => prev.map((c) => (c.id === categoryId ? { ...c, serviceFeeExempt } : c)))
    try {
      await updateCategory(categoryId, { serviceFeeExempt })
    } catch (err: any) {
      setSaveError(err.response?.data?.message || 'Failed to save — reverting')
      fetchCategories()
    } finally {
      setSavingId(null)
    }
  }

  const [serviceFeeValueDrafts, setServiceFeeValueDrafts] = useState<Record<string, string>>({})
  // Per-category Save button state (see the SAVE BUTTON note in the header).
  // Categories whose service-fee TYPE was switched to Percent / Flat $ but
  // not yet saved (the type alone is never saved without a value).
  const [unsavedServiceType, setUnsavedServiceType] = useState<Set<string>>(new Set())
  // Typed-but-not-yet-saved text in the two "Customer %" boxes.
  const [splitDrafts, setSplitDrafts] = useState<Record<string, { service?: string; processing?: string }>>({})
  const [saveStatus, setSaveStatus] = useState<Record<string, { state: 'saved' | 'error'; at: number; message?: string }>>({})

  const handleServiceFeeTypeChange = async (category: Category, type: 'default' | 'percent' | 'flat') => {
    if (!canModify) return
    if (type === 'default') {
      setServiceFeeValueDrafts((prev) => { const next = { ...prev }; delete next[category.id]; return next })
      setUnsavedServiceType((prev) => { const next = new Set(prev); next.delete(category.id); return next })
      if (category.serviceFeeType === null) return
      setSavingId(category.id)
      setSaveError(null)
      setCategories((prev) => prev.map((c) => (c.id === category.id ? { ...c, serviceFeeType: null, serviceFeeValue: null } : c)))
      try {
        await updateCategory(category.id, { serviceFeeType: null, serviceFeeValue: null })
      } catch (err: any) {
        setSaveError(err.response?.data?.message || 'Failed to save — reverting')
        fetchCategories()
      } finally {
        setSavingId(null)
      }
      return
    }
    setCategories((prev) => prev.map((c) => (c.id === category.id ? { ...c, serviceFeeType: type } : c)))
    // Nothing is stored yet — it needs a value. The Save button flags it.
    setUnsavedServiceType((prev) => new Set(prev).add(category.id))
    // Drop an earlier held "back to default" so it cannot override this choice.
    setStagedEdits((prev) => {
      if (!prev[category.id]) return prev
      const copy = { ...prev[category.id] }
      delete copy.serviceFeeType
      delete copy.serviceFeeValue
      return { ...prev, [category.id]: copy }
    })
  }

  const handleServiceFeeValueBlur = async (category: Category) => {
    if (!canModify) return
    const draft = serviceFeeValueDrafts[category.id]
    if (draft === undefined) return
    const trimmed = draft.trim()
    if (trimmed === '') return
    const newValue = Number(trimmed)
    if (!Number.isFinite(newValue) || newValue < 0) {
      setSaveError('Service fee value must be a non-negative number.')
      setServiceFeeValueDrafts((prev) => { const next = { ...prev }; delete next[category.id]; return next })
      return
    }
    if (newValue === category.serviceFeeValue) {
      setServiceFeeValueDrafts((prev) => { const next = { ...prev }; delete next[category.id]; return next })
      return
    }
    setSavingId(category.id)
    setSaveError(null)
    setCategories((prev) => prev.map((c) => (c.id === category.id ? { ...c, serviceFeeValue: newValue } : c)))
    setServiceFeeValueDrafts((prev) => { const next = { ...prev }; delete next[category.id]; return next })
    try {
      await updateCategory(category.id, { serviceFeeType: category.serviceFeeType, serviceFeeValue: newValue })
      setUnsavedServiceType((prev) => { const next = new Set(prev); next.delete(category.id); return next })
    } catch (err: any) {
      setSaveError(err.response?.data?.message || 'Failed to save — reverting')
      fetchCategories()
    } finally {
      setSavingId(null)
    }
  }

  // Service fee split — customer side drives the change and tasker
  // auto-adjusts to maintain a 100% sum.
  const handleServiceFeeSplitBlur = async (category: Category, rawCustomerPct: string) => {
    if (!canModify) return
    const parsed = parseInt(rawCustomerPct, 10)
    if (!Number.isInteger(parsed) || parsed < 0 || parsed > 100) {
      setSaveError('Service fee customer % must be an integer between 0 and 100.')
      return
    }
    const customerPct = clampPct(parsed)
    const taskerPct   = 100 - customerPct
    if (customerPct === category.serviceFeeCustomerPct) return
    setSavingId(category.id)
    setSaveError(null)
    setCategories((prev) => prev.map((c) => c.id === category.id ? { ...c, serviceFeeCustomerPct: customerPct, serviceFeeTaskerPct: taskerPct } : c))
    try {
      await updateCategory(category.id, { serviceFeeCustomerPct: customerPct, serviceFeeTaskerPct: taskerPct })
    } catch (err: any) {
      setSaveError(err.response?.data?.message || 'Failed to save — reverting')
      fetchCategories()
    } finally {
      setSavingId(null)
    }
  }

  // ── Processing fee split ────────────────────────────────────
  const handleProcessingFeeSplitBlur = async (category: Category, rawCustomerPct: string) => {
    if (!canModify) return
    const parsed = parseInt(rawCustomerPct, 10)
    if (!Number.isInteger(parsed) || parsed < 0 || parsed > 100) {
      setSaveError('Processing fee customer % must be an integer between 0 and 100.')
      return
    }
    const customerPct = clampPct(parsed)
    const taskerPct   = 100 - customerPct
    if (customerPct === category.processingFeeCustomerPct) return
    setSavingId(category.id)
    setSaveError(null)
    setCategories((prev) => prev.map((c) => c.id === category.id ? { ...c, processingFeeCustomerPct: customerPct, processingFeeTaskerPct: taskerPct } : c))
    try {
      await updateCategory(category.id, { processingFeeCustomerPct: customerPct, processingFeeTaskerPct: taskerPct })
    } catch (err: any) {
      setSaveError(err.response?.data?.message || 'Failed to save — reverting')
      fetchCategories()
    } finally {
      setSavingId(null)
    }
  }

  // ── Two-location / pickup code ──────────────────────────────
  const handleTwoLocationToggle = async (category: Category) => {
    if (!canModify) return
    const next = !category.isTwoLocation
    setSavingId(category.id)
    setCategories((prev) => prev.map((c) =>
      c.id === category.id
        ? { ...c, isTwoLocation: next, requiresPickupCode: next ? c.requiresPickupCode : false }
        : c
    ))
    try {
      await updateCategoryPickupFlags(category.id, { isTwoLocation: next })
    } catch (err) {
      // FIX: this reverted the optimistic toggle on failure (correct)
      // but gave no indication WHY — every other handler in this file
      // logs and calls setSaveError; this was the one that silently
      // snapped the checkbox back with nothing else, which reads as a
      // UI glitch rather than "your change wasn't saved."
      console.error('Failed to update two-location flag:', err)
      setSaveError((err as any)?.response?.data?.message || 'Failed to save — reverting')
      setCategories((prev) => prev.map((c) =>
        c.id === category.id
          ? { ...c, isTwoLocation: category.isTwoLocation, requiresPickupCode: category.requiresPickupCode }
          : c
      ))
    } finally {
      setSavingId(null)
    }
  }

  const handlePickupCodeToggle = async (category: Category) => {
    if (!canModify) return
    const next = !category.requiresPickupCode
    setSavingId(category.id)
    setCategories((prev) => prev.map((c) =>
      c.id === category.id
        ? { ...c, requiresPickupCode: next, isTwoLocation: next ? true : c.isTwoLocation }
        : c
    ))
    try {
      await updateCategoryPickupFlags(category.id, { requiresPickupCode: next })
    } catch (err) {
      console.error('Failed to update pickup-code flag:', err)
      setSaveError((err as any)?.response?.data?.message || 'Failed to save — reverting')
      setCategories((prev) => prev.map((c) =>
        c.id === category.id
          ? { ...c, requiresPickupCode: category.requiresPickupCode, isTwoLocation: category.isTwoLocation }
          : c
      ))
    } finally {
      setSavingId(null)
    }
  }

  // ── Insurance fee exempt toggle ─────────────────────────────
  const handleInsuranceFeeExemptToggle = async (categoryId: string, insuranceFeeExempt: boolean) => {
    if (!canModify) return
    setSavingId(categoryId)
    setCategories((prev) => prev.map((c) => (c.id === categoryId ? { ...c, insuranceFeeExempt } : c)))
    try {
      await updateCategory(categoryId, { insuranceFeeExempt })
    } catch (err: any) {
      setSaveError(err.response?.data?.message || 'Failed to save')
      fetchCategories()
    } finally {
      setSavingId(null)
    }
  }

  // ── Insurance coverage days (jobSpanDays) ───────────────────
  const handleJobSpanDaysChange = async (categoryId: string, raw: string) => {
    if (!canModify) return
    const days = Number(raw)
    if (!Number.isInteger(days) || days < 1 || days > 3650) {
      setSaveError('Coverage days must be a whole number from 1 to 3650.')
      fetchCategories()
      return
    }
    setSavingId(categoryId)
    setSaveError(null)
    setCategories((prev) => prev.map((c) => (c.id === categoryId ? { ...c, jobSpanDays: days } : c)))
    try {
      await updateCategory(categoryId, { jobSpanDays: days })
    } catch (err: any) {
      setSaveError(err.response?.data?.message || 'Failed to save')
      fetchCategories()
    } finally {
      setSavingId(null)
    }
  }

  // ── Insurance pricing source (manual rows or connected insurer) ──
  const handleInsurancePricingChange = async (categoryId: string, insurancePricing: 'manual' | 'insurer') => {
    if (!canModify) return
    setSavingId(categoryId)
    setCategories((prev) => prev.map((c) => (c.id === categoryId ? { ...c, insurancePricing } : c)))
    try {
      await updateCategory(categoryId, { insurancePricing })
    } catch (err: any) {
      setSaveError(err.response?.data?.message || 'Failed to save')
      fetchCategories()
    } finally {
      setSavingId(null)
    }
  }

  // ── Individual-expert liability insurance toggle ────────────
  // Turning this OFF also clears minLiabilityCoverageAmount server-
  // side is NOT done automatically here — the coverage amount is
  // only ever shown/edited while the toggle is on, and re-enabling
  // later restores whatever amount was last saved rather than
  // losing it, same "leave the data, just hide the control" pattern
  // insuranceFeeExempt already uses for its own rows above.
  const handleLiabilityInsuranceToggle = async (categoryId: string, requiresLiabilityInsurance: boolean) => {
    if (!canModify) return
    if (requiresLiabilityInsurance
      && !confirmMandatoryForIndividuals('liability insurance', categoryNameById(categoryId))) return
    setSavingId(categoryId)
    setCategories((prev) => prev.map((c) => (c.id === categoryId ? { ...c, requiresLiabilityInsurance } : c)))
    try {
      await updateCategory(categoryId, { requiresLiabilityInsurance })
    } catch (err: any) {
      setSaveError(err.response?.data?.message || 'Failed to save')
      fetchCategories()
    } finally {
      setSavingId(null)
    }
  }

  // ── Minimum liability coverage amount ───────────────────────
  // Same draft-then-commit-on-blur shape as commissionPercent above
  // — local text state while typing, parsed and saved only once the
  // admin leaves the field, so a half-typed number never gets sent.
  const handleMinLiabilityCoverageBlur = async (category: Category) => {
    const draft = liabilityCoverageDrafts[category.id]
    if (draft === undefined) return

    const trimmed = draft.trim()
    const newValue = trimmed === '' ? null : Number(trimmed)
    if (trimmed !== '' && (!Number.isFinite(newValue) || (newValue as number) < 0)) {
      setSaveError('Minimum coverage must be a positive number, or left blank')
      setLiabilityCoverageDrafts((prev) => { const next = { ...prev }; delete next[category.id]; return next })
      return
    }
    if (newValue === category.minLiabilityCoverageAmount) {
      setLiabilityCoverageDrafts((prev) => { const next = { ...prev }; delete next[category.id]; return next })
      return
    }
    setSavingId(category.id)
    setSaveError(null)
    setCategories((prev) => prev.map((c) => (c.id === category.id ? { ...c, minLiabilityCoverageAmount: newValue } : c)))
    setLiabilityCoverageDrafts((prev) => { const next = { ...prev }; delete next[category.id]; return next })
    try {
      await updateCategory(category.id, { minLiabilityCoverageAmount: newValue })
    } catch (err: any) {
      setSaveError(err.response?.data?.message || 'Failed to save — reverting')
      fetchCategories()
    } finally {
      setSavingId(null)
    }
  }

  // ── Per-category Save button ────────────────────────────────
  // Re-reads the list from the server WITHOUT the "Loading…" state, so rows
  // do not collapse or flash after a save.
  const refreshCategoriesQuietly = async () => {
    try {
      const res = await listAllCategoriesAdmin()
      setCategories(withStaged(res.data.data))
    } catch (err) {
      console.error('Failed to refresh categories after save:', err)
    }
  }

  const parseWholePct = (raw: string): number | null => {
    const trimmed = raw.trim()
    const n = Number(trimmed)
    return trimmed !== '' && Number.isInteger(n) && n >= 0 && n <= 100 ? n : null
  }

  // Everything typed or chosen for this category that is not stored yet.
  const pendingEdits = (category: Category): { fields: Record<string, any>; labels: string[]; problem: string | null } => {
    const id = category.id
    // Edits already held from toggles / dropdowns / blur-committed fields.
    const fields: Record<string, any> = { ...(stagedEdits[id] || {}) }
    const labels: string[] = Object.keys(fields).map((k) => STAGED_LABELS[k] ?? k)
    let problem: string | null = null

    // Commission % (custom value typed)
    const commissionDraft = commissionPercentDrafts[id]
    if (commissionDraft !== undefined) {
      const trimmed = commissionDraft.trim()
      const value = trimmed === '' ? null : Number(trimmed)
      if (value !== null && (!Number.isFinite(value) || value < 0 || value > 100)) {
        problem = 'Commission percent must be a number between 0 and 100, or blank for the platform default.'
      } else if (value !== category.commissionPercent) {
        fields.commissionPercent = value
        labels.push('Commission %')
      }
    }

    // Service fee type + value (type alone is never stored without a value)
    const valueDraft = serviceFeeValueDrafts[id]
    const typeUnsaved = unsavedServiceType.has(id)
    if (category.serviceFeeType != null && (typeUnsaved || valueDraft !== undefined)) {
      const raw = valueDraft !== undefined
        ? valueDraft.trim()
        : (category.serviceFeeValue != null ? String(category.serviceFeeValue) : '')
      const value = raw === '' ? null : Number(raw)
      if (value === null) {
        problem = problem ?? 'Enter a service fee value, or switch the service fee back to the platform default.'
      } else if (!Number.isFinite(value) || value < 0) {
        problem = problem ?? 'Service fee value must be a non-negative number.'
      } else if (typeUnsaved || value !== category.serviceFeeValue) {
        fields.serviceFeeType = category.serviceFeeType
        fields.serviceFeeValue = value
        labels.push('Service fee')
      }
    }

    // Service fee split
    const serviceSplit = splitDrafts[id]?.service
    if (serviceSplit !== undefined && !category.serviceFeeExempt) {
      const pct = parseWholePct(serviceSplit)
      if (pct === null) {
        problem = problem ?? 'Service fee customer % must be a whole number between 0 and 100.'
      } else if (pct !== category.serviceFeeCustomerPct) {
        fields.serviceFeeCustomerPct = pct
        fields.serviceFeeTaskerPct = 100 - pct
        labels.push('Service fee split')
      }
    }

    // Processing fee split
    const processingSplit = splitDrafts[id]?.processing
    if (processingSplit !== undefined) {
      const pct = parseWholePct(processingSplit)
      if (pct === null) {
        problem = problem ?? 'Processing fee customer % must be a whole number between 0 and 100.'
      } else if (pct !== category.processingFeeCustomerPct) {
        fields.processingFeeCustomerPct = pct
        fields.processingFeeTaskerPct = 100 - pct
        labels.push('Processing split')
      }
    }

    // Minimum liability coverage
    const coverageDraft = liabilityCoverageDrafts[id]
    if (coverageDraft !== undefined) {
      const trimmed = coverageDraft.trim()
      const value = trimmed === '' ? null : Number(trimmed)
      if (value !== null && (!Number.isFinite(value) || value < 0)) {
        problem = problem ?? 'Minimum coverage must be a positive number, or left blank.'
      } else if (value !== category.minLiabilityCoverageAmount) {
        fields.minLiabilityCoverageAmount = value
        labels.push('Minimum coverage')
      }
    }

    return { fields, labels: Array.from(new Set(labels)), problem }
  }

  const handleSaveCategory = async (category: Category) => {
    if (!canModify || savingId === category.id) return
    const id = category.id
    const { fields, problem } = pendingEdits(category)
    setSaveError(null)
    if (problem) {
      setSaveStatus((prev) => ({ ...prev, [id]: { state: 'error', at: Date.now(), message: problem } }))
      return
    }
    setSavingId(id)
    try {
      // Everything pending for this category: two-location / pickup-code first
      // (the server applies rules between them and the vehicle requirement),
      // then all the other fields — reach included — in one request.
      const pickup: Record<string, any> = {}
      const general: Record<string, any> = {}
      for (const [key, value] of Object.entries(fields)) {
        if (PICKUP_FLAG_KEYS.includes(key)) pickup[key] = value
        else general[key] = value
      }
      if (Object.keys(pickup).length > 0)  await updateCategoryPickupFlagsNow(id, pickup)
      if (Object.keys(general).length > 0) await updateCategoryNow(id, general)
      setCommissionPercentDrafts((prev) => { const next = { ...prev }; delete next[id]; return next })
      setServiceFeeValueDrafts((prev) => { const next = { ...prev }; delete next[id]; return next })
      setLiabilityCoverageDrafts((prev) => { const next = { ...prev }; delete next[id]; return next })
      setSplitDrafts((prev) => { const next = { ...prev }; delete next[id]; return next })
      setUnsavedServiceType((prev) => { const next = new Set(prev); next.delete(id); return next })
      // Saved — nothing is held for this category any more (ref updated now so
      // the re-read below does not lay the old edits back on top).
      const stillHeld = { ...stagedRef.current }
      delete stillHeld[id]
      stagedRef.current = stillHeld
      setStagedEdits(stillHeld)
      // Show what is really stored, not what this page assumes.
      await refreshCategoriesQuietly()
      setSaveStatus((prev) => ({ ...prev, [id]: { state: 'saved', at: Date.now() } }))
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to save — your changes are still here, try again.'
      setSaveStatus((prev) => ({ ...prev, [id]: { state: 'error', at: Date.now(), message } }))
      setSaveError(message)
    } finally {
      setSavingId(null)
    }
  }

  // Warn before closing / reloading the tab while something is unsaved.
  const hasUnsavedEdits = categories.some((c) => {
    const pending = pendingEdits(c)
    return pending.labels.length > 0 || pending.problem !== null
  })
  useEffect(() => {
    if (!hasUnsavedEdits) return
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [hasUnsavedEdits])

  // ── CategoryInsuranceFee row management ─────────────────────
  // Editor values for one insurance type: the saved row when there is
  // one, otherwise the defaults — 0% (nothing charged), tasker bears it.
  const draftForType = (categoryId: string, insuranceType: InsuranceType) => {
    const row = categories.find((c) => c.id === categoryId)?.insuranceFeeRows?.find((r) => r.insuranceType === insuranceType)
    return row
      ? {
          insuranceType,
          feeType:          row.feeType,
          feeValue:         String(row.feeValue),
          customerPct:      String(row.customerPct),
          taskerPct:        String(row.taskerPct),
          waivedIfVerified: row.waivedIfVerified,
        }
      : {
          insuranceType,
          feeType:          'percent' as FeeType,
          feeValue:         '0',
          customerPct:      '0',
          taskerPct:        '100',
          waivedIfVerified: true,
        }
  }

  const initNewRowDraft = (categoryId: string, insuranceType: InsuranceType = 'liability') => {
    setNewRowDraft((prev) => ({ ...prev, [categoryId]: draftForType(categoryId, insuranceType) }))
  }

  // Opens the editor on one type (from that type's "Edit" link).
  const openInsuranceEditor = (categoryId: string, insuranceType: InsuranceType) => {
    setExpandedInsuranceId(categoryId)
    initNewRowDraft(categoryId, insuranceType)
  }

  const handleUpsertInsuranceFeeRow = async (categoryId: string) => {
    if (!canModify) return
    const draft = newRowDraft[categoryId]
    if (!draft) return

    const feeValue = parseFloat(draft.feeValue)
    if (!Number.isFinite(feeValue) || feeValue < 0) {
      setSaveError('Fee value must be a non-negative number.')
      return
    }
    const customerPct = parseInt(draft.customerPct, 10)
    const taskerPct   = parseInt(draft.taskerPct, 10)
    if (!Number.isInteger(customerPct) || !Number.isInteger(taskerPct) || customerPct + taskerPct !== 100) {
      setSaveError('Customer % and Tasker % must be integers that sum to 100.')
      return
    }

    setSavingId(categoryId)
    setSaveError(null)
    try {
      await upsertCategoryInsuranceFee(categoryId, draft.insuranceType, {
        feeType:          draft.feeType,
        feeValue,
        customerPct,
        taskerPct,
        waivedIfVerified: draft.waivedIfVerified,
      })
      // Refresh so the new row appears in the list
      await fetchCategories()
      setNewRowDraft((prev) => { const next = { ...prev }; delete next[categoryId]; return next })
    } catch (err: any) {
      setSaveError(err.response?.data?.message || 'Failed to save insurance fee row')
    } finally {
      setSavingId(null)
    }
  }

  const handleDeleteInsuranceFeeRow = async (categoryId: string, insuranceType: InsuranceType) => {
    if (!canModify) return
    if (!window.confirm(`Remove the ${INSURANCE_TYPE_LABELS[insuranceType]} insurance fee row from this category?`)) return
    setSavingId(categoryId)
    setSaveError(null)
    try {
      await deleteCategoryInsuranceFee(categoryId, insuranceType)
      setCategories((prev) => prev.map((c) =>
        c.id === categoryId
          ? { ...c, insuranceFeeRows: (c.insuranceFeeRows || []).filter((r) => r.insuranceType !== insuranceType) }
          : c
      ))
    } catch (err: any) {
      setSaveError(err.response?.data?.message || 'Failed to delete insurance fee row')
    } finally {
      setSavingId(null)
    }
  }

  // ── CategoryLicenseRequirement management ───────────────────
  const loadRequirements = async (categoryId: string) => {
    setRequirementsLoading((prev) => ({ ...prev, [categoryId]: true }))
    setRequirementError(null)
    try {
      const res = await listCategoryLicenseRequirements(categoryId)
      setRequirementsByCategory((prev) => ({ ...prev, [categoryId]: res.data.data }))
    } catch (err: any) {
      setRequirementError(err.response?.data?.message || 'Failed to load licence requirements')
    } finally {
      setRequirementsLoading((prev) => ({ ...prev, [categoryId]: false }))
    }
  }

  const toggleRequirementsExpanded = (categoryId: string) => {
    const next = expandedRequirementsId === categoryId ? null : categoryId
    setExpandedRequirementsId(next)
    setRequirementError(null)
    if (next && !(categoryId in requirementsByCategory)) {
      loadRequirements(categoryId)
    }
  }

  const initNewRequirementDraft = (categoryId: string) => {
    setNewRequirementDraft((prev) => ({
      ...prev,
      // Insurance lines are "Optional for individuals" by default — making
      // one Mandatory asks for confirmation (see confirmMandatoryForIndividuals).
      [categoryId]: { docType: 'insurance_proof', label: '', description: '', insuranceType: '', verificationLevel: 'individual', isOptional: true },
    }))
  }

  // An Insurance Proof requirement must say which insurance it proves —
  // otherwise the system treats the upload as an ordinary document (no
  // policy form, no fee waiver, no individual/company insurance rule). The
  // server refuses it too (admin.service.js assertInsuranceProofHasType).
  const INSURANCE_TYPE_REQUIRED_MESSAGE =
    'Choose which insurance this proves ("Proves Insurance"). An Insurance Proof requirement cannot be left without one.'

  // ── Mandatory insurance for individuals: confirm + flag (2026-10-09) ──
  // Default: every insurance line is "Optional for individuals" — an
  // individual is not asked for it in onboarding and can add it in Profile →
  // Insurance to remove the fee. Companies must ALWAYS upload every line.
  // Making a line (or the liability checkbox) mandatory for individuals is a
  // deliberate choice, so it needs confirmation and the category is flagged.
  const confirmMandatoryForIndividuals = (what: string, categoryName: string) =>
    window.confirm(
      `Make ${what} MANDATORY for individuals in "${categoryName}"?\n\n` +
      'Individual taskers will have to upload it before they can finish onboarding in this category ' +
      '(normally individuals do not need insurance — it is optional and only removes the insurance fee). ' +
      'This category will be flagged on this page.\n\nCompanies always have to upload it either way.'
    )
  const isMandatoryIndividualInsuranceLine = (r: { insuranceType?: string | null; verificationLevel?: string | null; isOptional?: boolean }) =>
    !!r.insuranceType && (r.verificationLevel || 'individual') === 'individual' && r.isOptional !== true
  const categoryNameById = (id: string) => categories.find((c) => c.id === id)?.name || 'this category'
  // What makes individuals need insurance in this category. Uses the
  // requirements panel when it is loaded (always current), otherwise the
  // server's list.
  const individualInsuranceWarnings = (c: Category): string[] => {
    const loaded = requirementsByCategory[c.id]
    const lines = loaded
      ? loaded.filter((r) => isMandatoryIndividualInsuranceLine(r)).map((r) => r.label)
      : (c.mandatoryIndividualInsurance || []).map((r) => r.label)
    return [...(c.requiresLiabilityInsurance ? ['Liability insurance (checkbox)'] : []), ...lines]
  }

  const handleCreateRequirement = async (categoryId: string) => {
    if (!canModify) return
    const draft = newRequirementDraft[categoryId]
    if (!draft) return
    if (!draft.label.trim()) {
      setRequirementError('Label is required.')
      return
    }
    if (draft.docType === 'insurance_proof' && !draft.insuranceType) {
      setRequirementError(INSURANCE_TYPE_REQUIRED_MESSAGE)
      return
    }
    if (isMandatoryIndividualInsuranceLine(draft)
      && !confirmMandatoryForIndividuals(`"${draft.label.trim()}"`, categoryNameById(categoryId))) return
    setRequirementSavingId(categoryId)
    setRequirementError(null)
    try {
      await createCategoryLicenseRequirement(categoryId, {
        docType:       draft.docType,
        label:         draft.label.trim(),
        description:   draft.description.trim() || null,
        insuranceType: draft.insuranceType || null,
        verificationLevel: draft.verificationLevel,
        // Optional only applies to an individual-held insurance requirement.
        isOptional: !!draft.insuranceType && draft.verificationLevel === 'individual' && draft.isOptional,
      })
      await loadRequirements(categoryId)
      setNewRequirementDraft((prev) => { const next = { ...prev }; delete next[categoryId]; return next })
    } catch (err: any) {
      setRequirementError(err.response?.data?.message || 'Failed to create licence requirement')
    } finally {
      setRequirementSavingId(null)
    }
  }

  const startEditRequirement = (req: CategoryLicenseRequirement) => {
    setEditingRequirementId(req.id)
    setEditRequirementDraft({
      docType:       req.docType,
      label:         req.label,
      description:   req.description || '',
      insuranceType: req.insuranceType || '',
      verificationLevel: req.verificationLevel || 'individual',
      isOptional: req.isOptional === true,
    })
    setRequirementError(null)
  }

  const handleUpdateRequirement = async (categoryId: string, requirementId: string) => {
    if (!canModify || !editRequirementDraft) return
    if (!editRequirementDraft.label.trim()) {
      setRequirementError('Label is required.')
      return
    }
    if (editRequirementDraft.docType === 'insurance_proof' && !editRequirementDraft.insuranceType) {
      setRequirementError(INSURANCE_TYPE_REQUIRED_MESSAGE)
      return
    }
    {
      const before = (requirementsByCategory[categoryId] || []).find((r) => r.id === requirementId)
      const becomesMandatory = isMandatoryIndividualInsuranceLine(editRequirementDraft)
        && !(before && isMandatoryIndividualInsuranceLine(before))
      if (becomesMandatory
        && !confirmMandatoryForIndividuals(`"${editRequirementDraft.label.trim()}"`, categoryNameById(categoryId))) return
    }
    setRequirementSavingId(requirementId)
    setRequirementError(null)
    try {
      await updateCategoryLicenseRequirement(categoryId, requirementId, {
        docType:       editRequirementDraft.docType,
        label:         editRequirementDraft.label.trim(),
        description:   editRequirementDraft.description.trim() || null,
        insuranceType: editRequirementDraft.insuranceType || null,
        verificationLevel: editRequirementDraft.verificationLevel,
        isOptional: !!editRequirementDraft.insuranceType
          && editRequirementDraft.verificationLevel === 'individual'
          && editRequirementDraft.isOptional,
      })
      await loadRequirements(categoryId)
      setEditingRequirementId(null)
      setEditRequirementDraft(null)
    } catch (err: any) {
      setRequirementError(err.response?.data?.message || 'Failed to update licence requirement')
    } finally {
      setRequirementSavingId(null)
    }
  }

  // Backend refuses (409) if any document is already uploaded
  // against this requirement — the error message names the count so
  // admin knows to resolve those documents first rather than
  // wondering why the delete silently failed.
  const handleDeleteRequirement = async (categoryId: string, requirementId: string, label: string) => {
    if (!canModify) return
    if (!window.confirm(`Remove the "${label}" licence requirement from this category?`)) return
    setRequirementSavingId(requirementId)
    setRequirementError(null)
    try {
      await deleteCategoryLicenseRequirement(categoryId, requirementId)
      setRequirementsByCategory((prev) => ({
        ...prev,
        [categoryId]: (prev[categoryId] || []).filter((r) => r.id !== requirementId),
      }))
    } catch (err: any) {
      setRequirementError(err.response?.data?.message || 'Failed to delete licence requirement')
    } finally {
      setRequirementSavingId(null)
    }
  }

  // ── Filtering and grouping ──────────────────────────────────
  const filtered = categories.filter((c) =>
    !search.trim() ||
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.group || '').toLowerCase().includes(search.toLowerCase())
  )

  const grouped = filtered.reduce((acc, c) => {
    const key = c.group || 'Ungrouped'
    if (!acc[key]) acc[key] = []
    acc[key].push(c)
    return acc
  }, {} as Record<string, Category[]>)

  // ── Shared toggle button component ──────────────────────────
  const Toggle: React.FC<{ checked: boolean; onChange: () => void; disabled: boolean; color?: string }> = ({
    checked, onChange, disabled, color = 'bg-green-500',
  }) => (
    <button
      type="button"
      onClick={onChange}
      disabled={disabled}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 ${checked ? color : 'bg-gray-600'}`}
    >
      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : 'translate-x-0.5'}`} />
    </button>
  )

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Categories</h1>
          <p className="text-gray-400 text-sm mt-1">{categories.length} total — manage category flags, fees, and location options</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/communication')}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-brand-600/20 text-brand-400 hover:bg-brand-600/30 transition-colors text-sm font-medium"
          >
            💬 Communication Settings →
          </button>
          {!canModify && (
            <span className="px-3 py-1.5 text-xs rounded-full bg-yellow-600/20 text-yellow-400">View Only</span>
          )}
        </div>
      </div>

      <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by name or group..."
        className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-white placeholder-gray-500 focus:border-brand-500 focus:outline-none mb-4" />

      {/* Tabs — Category / Active / Comm stay visible in every tab
          (see comment on activeTab state above for why); everything
          else only renders under its matching tab. */}
      <div className="flex items-center gap-2 mb-6">
        {([
          { key: 'basics',    label: 'Basics' },
          { key: 'licensing', label: 'Licensing & Insurance' },
          { key: 'reach',     label: 'Reach & Escalation' },
          { key: 'payment',   label: 'Payment & Tax' },
        ] as const).map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? 'bg-brand-600 text-white'
                : 'bg-surface-tertiary text-gray-400 hover:text-white'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {saveError && (
        <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-4 mb-6 flex items-center justify-between">
          <p className="text-sm text-red-400">{saveError}</p>
          <button onClick={() => setSaveError(null)} className="text-red-400 hover:text-white text-sm">✕</button>
        </div>
      )}

      {/* Insurance defaults — one default per insurance type, copied into every category without its own value */}
      {activeTab === 'basics' && <InsuranceDefaultsPanel canModify={!!canModify} onChanged={fetchCategories} />}

      {/* Platform-wide fee defaults — every blank field in the table
          below falls back to these. Only shown on Basics, since that's
          the only tab with fee columns. */}
      {activeTab === 'basics' && (
        <div className="bg-surface-card border border-border-light rounded-2xl mb-6 overflow-hidden">
          <button
            onClick={() => setPlatformPanelOpen((o) => !o)}
            className="w-full flex items-center justify-between px-5 py-4 text-left"
          >
            <div>
              <h2 className="text-sm font-semibold text-white">Platform Defaults</h2>
              <p className="text-xs text-gray-500 mt-0.5">The fallback every category below uses when its own fee fields are blank</p>
            </div>
            <span className="text-gray-500 text-sm">{platformPanelOpen ? '▲ Hide' : '▼ Edit'}</span>
          </button>

          {platformPanelOpen && (
            <div className="px-5 pb-5 border-t border-border-light pt-4">
              {!platformSettings ? (
                <p className="text-sm text-gray-400">{platformError || 'Loading...'}</p>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {NUMERIC_PLATFORM_FIELDS.map((field) => {
                      const state = platformSettings[field.key]
                      const isOverridden = state.override != null
                      return (
                        <div key={field.key}>
                          <label className="block text-xs font-medium text-gray-400 mb-1">{field.label}</label>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              step={field.step}
                              disabled={!canModify}
                              value={platformDraft[field.key]}
                              onChange={(e) => setPlatformDraft({ ...platformDraft, [field.key]: e.target.value })}
                              placeholder={`Default: ${state.staticDefault}${field.suffix}`}
                              className="w-full bg-surface-tertiary border border-border-light rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50"
                            />
                            {field.suffix && <span className="text-gray-500 text-sm shrink-0">{field.suffix}</span>}
                          </div>
                          <p className="text-xs mt-1">
                            {isOverridden ? (
                              <span className="text-brand-400">Override — server default is {state.staticDefault}{field.suffix}</span>
                            ) : (
                              <span className="text-gray-500">Using server default ({state.staticDefault}{field.suffix})</span>
                            )}
                          </p>
                        </div>
                      )
                    })}

                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1">Service Fee Type</label>
                      <select
                        disabled={!canModify}
                        value={platformDraft.serviceFeeType}
                        onChange={(e) => setPlatformDraft({ ...platformDraft, serviceFeeType: e.target.value })}
                        className="w-full bg-surface-tertiary border border-border-light rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50"
                      >
                        <option value="">Default ({String(platformSettings.serviceFeeType.staticDefault)})</option>
                        <option value="percent">Percent of job price</option>
                        <option value="flat">Flat amount</option>
                      </select>
                      <p className="text-xs mt-1">
                        {platformSettings.serviceFeeType.override != null ? (
                          <span className="text-brand-400">Override — server default is {String(platformSettings.serviceFeeType.staticDefault)}</span>
                        ) : (
                          <span className="text-gray-500">Using server default ({String(platformSettings.serviceFeeType.staticDefault)})</span>
                        )}
                      </p>
                    </div>
                  </div>

                  {platformError && <p className="text-red-400 text-sm mt-4">{platformError}</p>}
                  {platformSaveMessage && <p className="text-green-400 text-sm mt-4">{platformSaveMessage}</p>}

                  {canModify && (
                    <div className="flex items-center gap-3 mt-4">
                      <button
                        onClick={handleSavePlatformSettings}
                        disabled={platformSaving}
                        className="px-4 py-2 bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-white text-sm font-medium rounded-xl transition-colors"
                      >
                        {platformSaving ? 'Saving...' : 'Save Defaults'}
                      </button>
                      {platformSettings.updatedAt && (
                        <p className="text-xs text-gray-500">
                          Last changed {new Date(platformSettings.updatedAt).toLocaleString()}
                          {platformSettings.updatedByUid ? ` by admin ${platformSettings.updatedByUid}` : ''}
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-gray-400">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-8 text-gray-400">No categories match your search</div>
      ) : (
        sortGroupNames(Object.keys(grouped), groupOrder).map((groupName) => {
          const groupCategories = grouped[groupName]
          return (
            <div key={groupName} className="mb-6">
              <h2 className="text-sm font-semibold text-gray-400 uppercase mb-3">{groupName}</h2>
              <div className="bg-surface-card border border-border-light rounded-2xl overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border-light">
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Category</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Active</th>
                      {activeTab === 'basics' && <>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Flags</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Location</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">BG Check</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Vulnerable Sector</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Two-Loc</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Pickup Code</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Expert Rate</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Pricing</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Platform Fee</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Service Fee</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Processing Split</th>
                      </>}
                      {activeTab === 'licensing' && (
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Insurance</th>
                      )}
                      {activeTab === 'reach' && (
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Reach & Escalation</th>
                      )}
                      {activeTab === 'payment' && <>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Payment channel</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Tax type</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">What this means</th>
                      </>}
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Comm</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupCategories.map((category) => (
                      <React.Fragment key={category.id}>
                        <tr className="border-b border-border-light last:border-0 hover:bg-surface-tertiary/50 transition-colors">
                          <td className="px-5 py-4">
                            <p className="text-sm font-medium text-white">{category.name}</p>
                            {individualInsuranceWarnings(category).length > 0 && (
                              <span
                                className="mt-1 inline-block px-2 py-0.5 text-xs rounded-full bg-amber-600/20 text-amber-400"
                                title={`Individuals must upload: ${individualInsuranceWarnings(category).join(', ')}. Normally insurance is optional for individuals — check this is intended.`}
                              >
                                ⚠️ Individuals must upload insurance ({individualInsuranceWarnings(category).length})
                              </span>
                            )}
                            {canModify && (() => {
                              const pending = pendingEdits(category)
                              const dirty = pending.labels.length > 0 || pending.problem !== null
                              const status = saveStatus[category.id]
                              const saving = savingId === category.id
                              return (
                                <div className="mt-2 flex flex-wrap items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() => handleSaveCategory(category)}
                                    disabled={saving}
                                    className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 ${
                                      dirty
                                        ? 'bg-brand-600 hover:bg-brand-500 text-white'
                                        : 'bg-surface-tertiary hover:bg-surface-tertiary/70 text-gray-300'
                                    }`}
                                  >
                                    {saving ? 'Saving…' : 'Save'}
                                  </button>
                                  {saving ? null
                                    : pending.problem ? <span className="text-xs text-red-400">{pending.problem}</span>
                                    : dirty && status?.state === 'error' ? <span className="text-xs text-red-400">{status.message}</span>
                                    : dirty ? <span className="text-xs text-amber-400">● Unsaved: {pending.labels.join(', ')}</span>
                                    : status?.state === 'saved' ? <span className="text-xs text-green-400">✓ Saved {new Date(status.at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>
                                    : <span className="text-xs text-gray-500">All changes saved</span>}
                                </div>
                              )
                            })()}
                          </td>

                          {/* Active */}
                          <td className="px-5 py-4">
                            {canModify ? (
                              <Toggle
                                checked={category.isActive}
                                onChange={() => handleActiveToggle(category.id, !category.isActive)}
                                disabled={savingId === category.id}
                              />
                            ) : (
                              <span className={`px-2 py-0.5 text-xs rounded-full ${category.isActive ? 'bg-green-600/20 text-green-400' : 'bg-red-600/20 text-red-400'}`}>
                                {category.isActive ? 'Active' : 'Inactive'}
                              </span>
                            )}
                          </td>

                          {activeTab === 'basics' && <>
                          {/* Flags */}
                          <td className="px-5 py-4">
                            <div className="flex flex-wrap gap-1">
                              {category.isInHome && <span className="px-2 py-0.5 text-xs rounded-full bg-surface-tertiary text-gray-300">In-Home</span>}
                              {category.requiresLicense && <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-600/20 text-yellow-400">License</span>}
                              {!category.isInHome && !category.requiresLicense && <span className="text-xs text-gray-600">—</span>}
                            </div>
                          </td>

                          {/* Location */}
                          <td className="px-5 py-4">
                            {canModify ? (
                              <div className="flex flex-col gap-1">
                                {(['allowsCustomerAddress', 'allowsExpertLocation', 'allowsVirtual'] as const).map((flag) => (
                                  <label key={flag} className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
                                    <input type="checkbox" checked={category[flag]}
                                      onChange={() => handleLocationFlagToggle(category, flag)}
                                      disabled={savingId === category.id}
                                      className="rounded border-border-light bg-surface-tertiary" />
                                    {flag === 'allowsCustomerAddress' ? 'Customer' : flag === 'allowsExpertLocation' ? 'Tasker' : 'Virtual'}
                                  </label>
                                ))}
                              </div>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                {category.allowsCustomerAddress && <span className="px-2 py-0.5 text-xs rounded-full bg-surface-tertiary text-gray-300">Customer</span>}
                                {category.allowsExpertLocation && <span className="px-2 py-0.5 text-xs rounded-full bg-surface-tertiary text-gray-300">Tasker</span>}
                                {category.allowsVirtual && <span className="px-2 py-0.5 text-xs rounded-full bg-surface-tertiary text-gray-300">Virtual</span>}
                              </div>
                            )}
                          </td>

                          {/* Background check */}
                          <td className="px-5 py-4">
                            {canModify ? (
                              <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
                                <input type="checkbox" checked={category.requiresBackgroundCheck}
                                  onChange={() => handleBackgroundCheckToggle(category.id, !category.requiresBackgroundCheck)}
                                  disabled={savingId === category.id}
                                  className="rounded border-border-light bg-surface-tertiary" />
                                Required
                              </label>
                            ) : (
                              <span className={`px-2 py-1 text-xs rounded-full ${category.requiresBackgroundCheck ? 'bg-yellow-600/20 text-yellow-400' : 'bg-surface-tertiary text-gray-300'}`}>
                                {category.requiresBackgroundCheck ? 'Required' : 'No'}
                              </span>
                            )}
                          </td>

                          {/* Vulnerable Sector Check */}
                          <td className="px-5 py-4">
                            {canModify ? (
                              <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
                                <input type="checkbox" checked={!!category.requiresVulnerableSectorCheck}
                                  onChange={() => handleVulnerableSectorToggle(category.id, !category.requiresVulnerableSectorCheck)}
                                  disabled={savingId === category.id}
                                  className="rounded border-border-light bg-surface-tertiary" />
                                Required
                              </label>
                            ) : (
                              <span className={`px-2 py-1 text-xs rounded-full ${category.requiresVulnerableSectorCheck ? 'bg-yellow-600/20 text-yellow-400' : 'bg-surface-tertiary text-gray-300'}`}>
                                {category.requiresVulnerableSectorCheck ? 'Required' : 'No'}
                              </span>
                            )}
                          </td>

                          {/* Two-location */}
                          <td className="px-5 py-4">
                            {canModify ? (
                              <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
                                <input type="checkbox" checked={category.isTwoLocation}
                                  onChange={() => handleTwoLocationToggle(category)}
                                  disabled={savingId === category.id}
                                  className="rounded border-border-light bg-surface-tertiary" />
                                {category.isTwoLocation ? 'On' : 'Off'}
                              </label>
                            ) : (
                              <span className={`px-2 py-1 text-xs rounded-full ${category.isTwoLocation ? 'bg-blue-600/20 text-blue-400' : 'bg-surface-tertiary text-gray-500'}`}>
                                {category.isTwoLocation ? '📍 On' : '—'}
                              </span>
                            )}
                          </td>

                          {/* Pickup code */}
                          <td className="px-5 py-4">
                            {canModify ? (
                              <label className={`flex items-center gap-2 text-xs cursor-pointer ${category.isTwoLocation ? 'text-gray-300' : 'text-gray-600 cursor-not-allowed'}`}>
                                <input type="checkbox" checked={category.requiresPickupCode}
                                  onChange={() => category.isTwoLocation && handlePickupCodeToggle(category)}
                                  disabled={savingId === category.id || !category.isTwoLocation}
                                  className="rounded border-border-light bg-surface-tertiary disabled:opacity-40" />
                                {category.requiresPickupCode ? 'On' : 'Off'}
                              </label>
                            ) : (
                              <span className={`px-2 py-1 text-xs rounded-full ${category.requiresPickupCode ? 'bg-orange-600/20 text-orange-400' : 'bg-surface-tertiary text-gray-500'}`}>
                                {category.requiresPickupCode ? '🔢 On' : '—'}
                              </span>
                            )}
                          </td>

                          {/* Expert Rate (pricing modes) */}
                          <td className="px-5 py-4">
                            {canModify ? (
                              <div className="flex flex-col gap-1">
                                {(['allowsHourlyPricing', 'allowsFlatPricing', 'allowsFreeToConnect'] as const).map((field) => (
                                  <label key={field} className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
                                    <input type="checkbox" checked={category[field]}
                                      onChange={() => handlePricingToggle(category.id, field, !category[field])}
                                      disabled={savingId === category.id}
                                      className="rounded border-border-light bg-surface-tertiary" />
                                    {field === 'allowsHourlyPricing' ? '⏱ Hourly' : field === 'allowsFlatPricing' ? '💰 Flat' : '🤝 Free'}
                                  </label>
                                ))}
                              </div>
                            ) : (
                              <div className="flex flex-col gap-0.5">
                                {category.allowsHourlyPricing && <span className="px-2 py-0.5 text-xs rounded-full bg-surface-tertiary text-gray-300">⏱ Hourly</span>}
                                {category.allowsFlatPricing && <span className="px-2 py-0.5 text-xs rounded-full bg-blue-600/20 text-blue-400">💰 Flat</span>}
                                {category.allowsFreeToConnect && <span className="px-2 py-0.5 text-xs rounded-full bg-purple-600/20 text-purple-400">🤝 Free</span>}
                              </div>
                            )}
                          </td>

                          {/* Pricing ($0 hire only — commission moved to its own Platform Fee column) */}
                          <td className="px-5 py-4">
                            {canModify ? (
                              <div className="flex flex-col gap-1">
                                <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
                                  <input type="checkbox" checked={category.allowsZeroPrice}
                                    onChange={() => handleAllowsZeroPriceToggle(category.id, !category.allowsZeroPrice)}
                                    disabled={savingId === category.id}
                                    className="rounded border-border-light bg-surface-tertiary" />
                                  Allow $0 hire
                                </label>
                              </div>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                {category.allowsZeroPrice && <span className="px-2 py-0.5 text-xs rounded-full bg-surface-tertiary text-gray-300">$0 ok</span>}
                                {!category.allowsZeroPrice && <span className="text-xs text-gray-600">—</span>}
                              </div>
                            )}
                          </td>

                          {/* Platform Fee (commission) — styled to match the Service Fee column */}
                          <td className="px-5 py-4">
                            {canModify ? (
                              <div className="flex flex-col gap-1">
                                <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
                                  <input type="checkbox" checked={category.commissionExempt}
                                    onChange={() => handleCommissionExemptToggle(category.id, !category.commissionExempt)}
                                    disabled={savingId === category.id}
                                    className="rounded border-border-light bg-surface-tertiary" />
                                  Exempt
                                </label>
                                {!category.commissionExempt && (
                                  <>
                                    <select
                                      value={commissionRateSelection(category)}
                                      onChange={(e) => handleCommissionRateSelect(category, e.target.value)}
                                      disabled={savingId === category.id}
                                      className="bg-surface-tertiary border border-border-light rounded-lg px-2 py-1 text-xs text-white focus:outline-none disabled:opacity-50"
                                    >
                                      <option value="default">{platformCommissionDefaultLabel()}</option>
                                      {PRESET_RATES.map((rate) => (
                                        <option key={rate} value={rate}>{rate}%</option>
                                      ))}
                                      <option value="custom">Custom…</option>
                                    </select>
                                    {commissionRateSelection(category) === 'custom' && (
                                      <label className="flex items-center gap-2 text-xs text-gray-300">
                                        <input
                                          type="text" inputMode="decimal" placeholder="e.g. 12.5" autoFocus
                                          value={commissionPercentDrafts[category.id] ?? (category.commissionPercent ?? '')}
                                          onChange={(e) => setCommissionPercentDrafts((prev) => ({ ...prev, [category.id]: e.target.value }))}
                                          onBlur={() => handleCommissionPercentBlur(category)}
                                          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
                                          disabled={savingId === category.id}
                                          className="w-16 bg-surface-tertiary border border-border-light rounded-lg px-2 py-1 text-xs text-white focus:outline-none disabled:opacity-50"
                                        />
                                        %
                                      </label>
                                    )}
                                  </>
                                )}
                              </div>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                {category.commissionExempt && <span className="px-2 py-0.5 text-xs rounded-full bg-green-600/20 text-green-400">Exempt</span>}
                                {!category.commissionExempt && category.commissionPercent != null && <span className="px-2 py-0.5 text-xs rounded-full bg-blue-600/20 text-blue-400">{category.commissionPercent}%</span>}
                                {!category.commissionExempt && category.commissionPercent == null && <span className="text-xs text-gray-600">{platformCommissionDefaultLabel()}</span>}
                              </div>
                            )}
                          </td>

                          {/* Service fee (with split) */}
                          <td className="px-5 py-4">
                            {canModify ? (
                              <div className="flex flex-col gap-1">
                                <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
                                  <input type="checkbox" checked={category.serviceFeeExempt}
                                    onChange={() => handleServiceFeeExemptToggle(category.id, !category.serviceFeeExempt)}
                                    disabled={savingId === category.id}
                                    className="rounded border-border-light bg-surface-tertiary" />
                                  Exempt
                                </label>
                                {!category.serviceFeeExempt && (
                                  <>
                                    <select
                                      value={category.serviceFeeType ?? 'default'}
                                      onChange={(e) => handleServiceFeeTypeChange(category, e.target.value as 'default' | 'percent' | 'flat')}
                                      disabled={savingId === category.id}
                                      className="bg-surface-tertiary border border-border-light rounded-lg px-2 py-1 text-xs text-white focus:outline-none disabled:opacity-50"
                                    >
                                      <option value="default">{platformServiceFeeDefaultLabel()}</option>
                                      <option value="percent">Percent</option>
                                      <option value="flat">Flat $</option>
                                    </select>
                                    {category.serviceFeeType != null && (
                                      <label className="flex items-center gap-2 text-xs text-gray-300">
                                        {category.serviceFeeType === 'flat' && '$'}
                                        <input type="text" inputMode="decimal"
                                          placeholder={category.serviceFeeType === 'flat' ? 'e.g. 2.00' : 'e.g. 3'}
                                          value={serviceFeeValueDrafts[category.id] ?? (category.serviceFeeValue ?? '')}
                                          onChange={(e) => setServiceFeeValueDrafts((prev) => ({ ...prev, [category.id]: e.target.value }))}
                                          onBlur={() => handleServiceFeeValueBlur(category)}
                                          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
                                          disabled={savingId === category.id}
                                          className="w-16 bg-surface-tertiary border border-border-light rounded-lg px-2 py-1 text-xs text-white focus:outline-none disabled:opacity-50"
                                        />
                                        {category.serviceFeeType === 'percent' && '%'}
                                      </label>
                                    )}
                                    {/* Split — customer % entry, tasker auto-computes */}
                                    <div className="flex items-center gap-1 text-xs text-gray-400 mt-1">
                                      <span>Customer %:</span>
                                      <input type="text" inputMode="numeric"
                                        defaultValue={category.serviceFeeCustomerPct}
                                        onChange={(e) => setSplitDrafts((prev) => ({ ...prev, [category.id]: { ...prev[category.id], service: e.target.value } }))}
                                        onBlur={(e) => handleServiceFeeSplitBlur(category, e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
                                        disabled={savingId === category.id}
                                        title="Percent of this fee charged to the customer"
                                        className="w-10 bg-surface-tertiary border border-border-light rounded px-1 py-0.5 text-xs text-white focus:outline-none"
                                      />
                                      <span>Tasker %: {category.serviceFeeTaskerPct}</span>
                                    </div>
                                  </>
                                )}
                              </div>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                {category.serviceFeeExempt && <span className="px-2 py-0.5 text-xs rounded-full bg-green-600/20 text-green-400">Exempt</span>}
                                {!category.serviceFeeExempt && category.serviceFeeType != null && (
                                  <span className="px-2 py-0.5 text-xs rounded-full bg-blue-600/20 text-blue-400">
                                    {category.serviceFeeType === 'flat' ? `$${category.serviceFeeValue}` : `${category.serviceFeeValue}%`}
                                  </span>
                                )}
                                {!category.serviceFeeExempt && category.serviceFeeType == null && <span className="text-xs text-gray-600">Default</span>}
                                {!category.serviceFeeExempt && (
                                  <span className="text-xs text-gray-500">{category.serviceFeeCustomerPct}% customer</span>
                                )}
                              </div>
                            )}
                          </td>

                          {/* Processing fee split */}
                          <td className="px-5 py-4">
                            {canModify ? (
                              <div className="flex items-center gap-1 text-xs text-gray-400">
                                <span>Customer %:</span>
                                <input type="text" inputMode="numeric"
                                  defaultValue={category.processingFeeCustomerPct}
                                  onChange={(e) => setSplitDrafts((prev) => ({ ...prev, [category.id]: { ...prev[category.id], processing: e.target.value } }))}
                                  onBlur={(e) => handleProcessingFeeSplitBlur(category, e.target.value)}
                                  onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
                                  disabled={savingId === category.id}
                                  title="Percent of this fee charged to the customer"
                                  className="w-10 bg-surface-tertiary border border-border-light rounded px-1 py-0.5 text-xs text-white focus:outline-none"
                                />
                                <span>Tasker %: {category.processingFeeTaskerPct}</span>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">
                                {category.processingFeeCustomerPct}% customer / {category.processingFeeTaskerPct}% tasker
                              </span>
                            )}
                          </td>
                          </>}

                          {activeTab === 'licensing' && (
                          <td className="px-5 py-4 min-w-[180px]">
                            {canModify ? (
                              <div className="space-y-1.5">
                                <label className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer">
                                  <input type="checkbox" checked={category.insuranceFeeExempt}
                                    onChange={() => handleInsuranceFeeExemptToggle(category.id, !category.insuranceFeeExempt)}
                                    disabled={savingId === category.id}
                                    className="accent-green-500" />
                                  Exempt
                                </label>
                                <label className="block text-xs text-gray-400" title="How many days a job's insurance covers it, counted from the scheduled day. 1 = that day only. Longer for ongoing care. Set this to match your insurer.">
                                  Coverage days{' '}
                                  <input
                                    type="number" min={1} max={3650} step={1}
                                    key={`${category.id}-${category.jobSpanDays ?? 1}`}
                                    defaultValue={category.jobSpanDays ?? 1}
                                    onBlur={(e) => { if (e.target.value !== String(category.jobSpanDays ?? 1)) handleJobSpanDaysChange(category.id, e.target.value) }}
                                    disabled={savingId === category.id}
                                    className="w-16 rounded border border-border-light bg-surface-tertiary px-1 py-0.5 text-xs text-gray-200" />
                                </label>
                                {!category.insuranceFeeExempt && (
                                  <>
                                    <label className="block text-xs text-gray-400" title="Insurer = the live price from the connected insurer for each job. If the insurer is off or can't answer, the fees below are used.">
                                      Pricing{' '}
                                      <select
                                        value={category.insurancePricing || 'manual'}
                                        onChange={(e) => handleInsurancePricingChange(category.id, e.target.value as 'manual' | 'insurer')}
                                        disabled={savingId === category.id}
                                        className="bg-gray-700 text-white rounded px-1 py-0.5 text-xs ml-1"
                                      >
                                        <option value="manual">Manual fees</option>
                                        <option value="insurer">Insurer</option>
                                      </select>
                                    </label>
                                    {/* All five types, always. A type with no row is
                                        0% — not charged (no row = no fee). */}
                                    <div className="space-y-0.5">
                                      {INSURANCE_TYPES.map((type) => {
                                        const row = (category.insuranceFeeRows || []).find((r) => r.insuranceType === type)
                                        return (
                                          <div key={type} className="flex items-center justify-between text-xs">
                                            <span className={row ? 'text-gray-300' : 'text-gray-500'}>{INSURANCE_TYPE_LABELS[type]}</span>
                                            {row ? (
                                              <span className="text-gray-500 ml-1" title={`Customer ${row.customerPct}% / Tasker ${row.taskerPct}%`}>
                                                {row.feeType === 'flat' ? `$${row.feeValue}` : `${row.feeValue}%`}
                                                {' '}(Cust {row.customerPct}% / Tasker {row.taskerPct}%)
                                                {row.waivedIfVerified && ' ✓'}
                                              </span>
                                            ) : (
                                              <span className="text-gray-600 ml-1" title="No rate set — nothing is charged for this type">0% — not charged</span>
                                            )}
                                            <span className="flex items-center ml-1 shrink-0">
                                              <button
                                                onClick={() => openInsuranceEditor(category.id, type)}
                                                disabled={savingId === category.id}
                                                className="text-brand-400 hover:text-brand-300 disabled:opacity-40"
                                                title={row ? 'Edit this rate' : 'Set a rate for this type'}
                                              >Edit</button>
                                              {row && (
                                                <button
                                                  onClick={() => handleDeleteInsuranceFeeRow(category.id, row.insuranceType)}
                                                  disabled={savingId === category.id}
                                                  className="text-red-400 hover:text-red-300 ml-1 disabled:opacity-40"
                                                  title="Remove this rate (back to 0% — not charged)"
                                                >✕</button>
                                              )}
                                            </span>
                                          </div>
                                        )
                                      })}
                                    </div>
                                    {expandedInsuranceId === category.id && (
                                      <button
                                        onClick={() => setExpandedInsuranceId(null)}
                                        className="text-xs text-brand-400 hover:text-brand-300"
                                      >
                                        − Close editor
                                      </button>
                                    )}
                                  </>
                                )}
                                {/* Licence checklist — separate from the fee rows above.
                                    This is where insuranceType gets set on a requirement,
                                    so it's shown regardless of insuranceFeeExempt: a
                                    category can have a licence checklist independent of
                                    whether it charges an insurance fee. */}
                                <button
                                  onClick={() => toggleRequirementsExpanded(category.id)}
                                  className="text-xs text-gray-400 hover:text-gray-300 block"
                                >
                                  {expandedRequirementsId === category.id ? '− Close Requirements' : '📋 Licence Requirements'}
                                </button>

                                {/* ── Individual-expert liability insurance ──────────
                                    Separate from insuranceFeeExempt / insuranceFeeRows
                                    above, which are the fee-split model. This is a
                                    checklist requirement — does an INDIVIDUAL expert
                                    also need to prove liability coverage to onboard,
                                    independent of whether a fee is charged for it. */}
                                <div className="pt-1.5 mt-1.5 border-t border-border-light">
                                  <label className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer">
                                    <input type="checkbox" checked={category.requiresLiabilityInsurance}
                                      onChange={() => handleLiabilityInsuranceToggle(category.id, !category.requiresLiabilityInsurance)}
                                      disabled={savingId === category.id}
                                      className="accent-brand-500" />
                                    🛡️ Require liability insurance (individuals)
                                  </label>
                                  {category.requiresLiabilityInsurance && (
                                    <div className="mt-1.5 flex items-center gap-1.5">
                                      <span className="text-xs text-gray-500">Min $</span>
                                      <input
                                        type="text"
                                        inputMode="numeric"
                                        placeholder="e.g. 2000000"
                                        value={
                                          liabilityCoverageDrafts[category.id] ??
                                          (category.minLiabilityCoverageAmount != null ? String(category.minLiabilityCoverageAmount) : '')
                                        }
                                        onChange={(e) => setLiabilityCoverageDrafts((prev) => ({ ...prev, [category.id]: e.target.value }))}
                                        onBlur={() => handleMinLiabilityCoverageBlur(category)}
                                        disabled={savingId === category.id}
                                        className="w-24 bg-surface-tertiary border border-border-light rounded px-1.5 py-0.5 text-xs text-white focus:border-brand-500 focus:outline-none"
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div className="flex flex-col gap-1">
                                <div className="flex flex-wrap gap-1">
                                  {category.insuranceFeeExempt ? (
                                    <span className="px-2 py-0.5 text-xs rounded-full bg-green-600/20 text-green-400">Exempt</span>
                                  ) : (
                                    // All five types; one with no row is 0% (not charged).
                                    INSURANCE_TYPES.map((type) => {
                                      const row = (category.insuranceFeeRows || []).find((r) => r.insuranceType === type)
                                      return (
                                        <span key={type} className={`px-2 py-0.5 text-xs rounded-full ${row ? 'bg-blue-600/20 text-blue-400' : 'bg-surface-tertiary text-gray-500'}`}>
                                          {INSURANCE_TYPE_LABELS[type]}: {row ? (row.feeType === 'flat' ? `$${row.feeValue}` : `${row.feeValue}%`) : '0%'}
                                        </span>
                                      )
                                    })
                                  )}
                                  {category.requiresLiabilityInsurance && (
                                    <span
                                      className="px-2 py-0.5 text-xs rounded-full bg-purple-600/20 text-purple-400"
                                      title={
                                        category.minLiabilityCoverageAmount
                                          ? `Individuals must prove at least $${category.minLiabilityCoverageAmount.toLocaleString()} liability coverage`
                                          : 'Individuals must prove liability coverage'
                                      }
                                    >
                                      🛡️ Liability required
                                    </span>
                                  )}
                                </div>
                                {/* View-only role can still see the checklist —
                                    just no create/edit/delete controls once
                                    expanded (canModify gates those below). */}
                                <button
                                  onClick={() => toggleRequirementsExpanded(category.id)}
                                  className="text-xs text-gray-400 hover:text-gray-300 text-left"
                                >
                                  {expandedRequirementsId === category.id ? '− Close Requirements' : '📋 Licence Requirements'}
                                </button>
                              </div>
                            )}
                          </td>
                          )}

                          {activeTab === 'reach' && (
                          <td className="px-5 py-4">
                            <button
                              onClick={() => setExpandedReachId(expandedReachId === category.id ? null : category.id)}
                              className="text-xs text-gray-400 hover:text-gray-300 block"
                            >
                              {expandedReachId === category.id ? '− Close Reach & Escalation' : '🗺️ Reach & Escalation'}
                            </button>
                          </td>
                          )}

                          {activeTab === 'payment' && <>
                          {/* Payment channel */}
                          <td className="px-5 py-4 min-w-[210px]">
                            {canModify ? (
                              <select value={category.paymentChannel}
                                onChange={(e) => handleTaxChannelChange(category.id, 'paymentChannel', e.target.value)}
                                disabled={savingId === category.id}
                                className="w-full text-xs rounded-lg border border-border-light bg-surface-tertiary text-gray-200 px-2 py-1.5">
                                <option value="INTERNAL">Internal: paid through the app</option>
                                <option value="EXTERNAL">External: free consultation (no payment)</option>
                              </select>
                            ) : (
                              <span className={`px-2 py-0.5 text-xs rounded-full ${category.paymentChannel === 'EXTERNAL' ? 'bg-amber-600/20 text-amber-400' : 'bg-surface-tertiary text-gray-300'}`}>
                                {category.paymentChannel === 'EXTERNAL' ? 'External' : 'Internal'}
                              </span>
                            )}
                          </td>

                          {/* Tax type */}
                          <td className="px-5 py-4 min-w-[210px]">
                            {canModify ? (
                              <select value={category.taxType}
                                onChange={(e) => handleTaxChannelChange(category.id, 'taxType', e.target.value)}
                                disabled={savingId === category.id}
                                className="w-full text-xs rounded-lg border border-border-light bg-surface-tertiary text-gray-200 px-2 py-1.5">
                                <option value="STANDARD">Standard: tax only if the tasker is GST/HST registered</option>
                                <option value="EXEMPT">Exempt: no job tax</option>
                                <option value="ZERO_RATED">Zero-rated: no job tax</option>
                                <option value="MANDATORY_REGISTRATION">Mandatory registration: tax always applies</option>
                                <option value="TRANSACTION_DEPENDENT">Transaction-dependent: no job tax for now</option>
                              </select>
                            ) : (
                              <span className="px-2 py-0.5 text-xs rounded-full bg-surface-tertiary text-gray-300">{category.taxType.replace(/_/g, ' ').toLowerCase()}</span>
                            )}
                          </td>

                          {/* Effects, so the admin sees exactly what the two choices change */}
                          <td className="px-5 py-4 min-w-[320px]">
                            <ul className="text-xs text-gray-400 space-y-0.5 list-disc pl-4">
                              {category.paymentChannel === 'EXTERNAL' ? <>
                                <li>Booking keeps the normal flow (apply, shortlist, chat, schedule, confirm). Customer sees Confirm Booking.</li>
                                <li>Every amount is $0: no card charge, no fees, no tax, no Stripe payment.</li>
                                <li>Price negotiation, budget and price fields are hidden.</li>
                                <li>Customer needs no card to post. Tasker is not paid for the call.</li>
                                <li>Onboarding: a tasker whose every selected category is External skips the bank (payout) step. One Internal category brings it back.</li>
                              </> : <>
                                <li>Customer pays by card when they hire; platform fees, processing fee and tasker payout apply.</li>
                                <li>Price negotiation and budget are available.</li>
                                <li>Tasker must connect a bank account (payout step in onboarding).</li>
                                <li>
                                  {category.taxType === 'STANDARD' && 'Job tax is added only for taskers who declared GST/HST registration, at the rate of the province the job is in (GST 5%, HST 13-15%, or GST + QST in Quebec).'}
                                  {category.taxType === 'MANDATORY_REGISTRATION' && 'Job tax is always added (the tasker must be registered), at the rate of the province the job is in.'}
                                  {(category.taxType === 'EXEMPT' || category.taxType === 'ZERO_RATED') && 'No tax is added to the job price.'}
                                  {category.taxType === 'TRANSACTION_DEPENDENT' && 'No tax is added to the job price for now (needs a per-booking decision).'}
                                </li>
                              </>}
                              <li className="text-gray-500">Applies to new bookings. Bookings already made keep the terms they were made with.</li>
                            </ul>
                          </td>
                          </>}

                          {/* Communication link */}
                          <td className="px-5 py-4">
                            <button
                              onClick={() => navigate('/communication')}
                              className="flex items-center gap-1.5 text-xs text-brand-400 hover:text-brand-300 transition-colors"
                            >
                              💬 →
                            </button>
                          </td>
                        </tr>

                        {/* ── Inline insurance row editor ── */}
                        {expandedInsuranceId === category.id && canModify && !category.insuranceFeeExempt && newRowDraft[category.id] && (
                          <tr className="border-b border-border-light bg-surface-tertiary/30">
                            {/* FIX: was colSpan={13} — hardcoded to the old always-13-column
                                layout. Now only some columns render per tab, so span
                                everything (same pattern already used by the Reach &
                                Escalation panel below). */}
                            <td colSpan={99} className="px-5 py-4">
                              <div className="bg-surface-card border border-border-light rounded-xl p-4">
                                <p className="text-xs font-semibold text-gray-300 mb-3">Set Insurance Rate — {INSURANCE_TYPE_LABELS[newRowDraft[category.id].insuranceType]}</p>
                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                                  <div>
                                    <label className="block text-xs text-gray-500 mb-1">Type</label>
                                    <select
                                      value={newRowDraft[category.id].insuranceType}
                                      // Load the chosen type's own saved values (or 0) — never
                                      // carry the previous type's rate over to this one.
                                      onChange={(e) => initNewRowDraft(category.id, e.target.value as InsuranceType)}
                                      className="w-full bg-surface-tertiary border border-border-light rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none"
                                    >
                                      {INSURANCE_TYPES.map((t) => (
                                        <option key={t} value={t}>{INSURANCE_TYPE_LABELS[t]}</option>
                                      ))}
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-xs text-gray-500 mb-1">Fee Type</label>
                                    <select
                                      value={newRowDraft[category.id].feeType}
                                      onChange={(e) => setNewRowDraft((prev) => ({
                                        ...prev,
                                        [category.id]: { ...prev[category.id], feeType: e.target.value as FeeType },
                                      }))}
                                      className="w-full bg-surface-tertiary border border-border-light rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none"
                                    >
                                      <option value="percent">Percent</option>
                                      <option value="flat">Flat $</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-xs text-gray-500 mb-1">
                                      Value {newRowDraft[category.id].feeType === 'flat' ? '($)' : '(%)'}
                                    </label>
                                    <input
                                      type="text" inputMode="decimal"
                                      placeholder="0"
                                      value={newRowDraft[category.id].feeValue}
                                      onChange={(e) => setNewRowDraft((prev) => ({
                                        ...prev,
                                        [category.id]: { ...prev[category.id], feeValue: e.target.value },
                                      }))}
                                      className="w-full bg-surface-tertiary border border-border-light rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs text-gray-500 mb-1">Customer %</label>
                                    <input
                                      type="text" inputMode="numeric" placeholder="0"
                                      value={newRowDraft[category.id].customerPct}
                                      onChange={(e) => {
                                        const c = e.target.value
                                        const t = String(100 - (parseInt(c, 10) || 0))
                                        setNewRowDraft((prev) => ({
                                          ...prev,
                                          [category.id]: { ...prev[category.id], customerPct: c, taskerPct: t },
                                        }))
                                      }}
                                      className="w-full bg-surface-tertiary border border-border-light rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs text-gray-500 mb-1">Tasker % (auto)</label>
                                    <input
                                      type="text" readOnly
                                      value={newRowDraft[category.id].taskerPct}
                                      className="w-full bg-surface-tertiary/50 border border-border-light rounded-lg px-2 py-1.5 text-xs text-gray-400"
                                    />
                                  </div>
                                  <div className="flex flex-col justify-between">
                                    <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer mt-4">
                                      <input
                                        type="checkbox"
                                        checked={newRowDraft[category.id].waivedIfVerified}
                                        onChange={(e) => setNewRowDraft((prev) => ({
                                          ...prev,
                                          [category.id]: { ...prev[category.id], waivedIfVerified: e.target.checked },
                                        }))}
                                        className="accent-brand-500"
                                      />
                                      Waive if verified
                                    </label>
                                  </div>
                                </div>
                                <p className="text-xs text-gray-500 mt-2 mb-3">
                                  0 = not charged (the default for every type). Customer % + Tasker % must equal 100. Default: 0% customer / 100% tasker (tasker bears the cost).
                                  "Waive if verified" means the row is $0 for both sides when the expert holds a valid policy of this type.
                                </p>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => handleUpsertInsuranceFeeRow(category.id)}
                                    disabled={savingId === category.id}
                                    className="px-4 py-2 text-sm bg-brand-600 text-white rounded-xl hover:bg-brand-500 disabled:opacity-50 transition-colors"
                                  >
                                    {savingId === category.id ? 'Saving…' : 'Save Rate'}
                                  </button>
                                  <button
                                    onClick={() => {
                                      setExpandedInsuranceId(null)
                                      setNewRowDraft((prev) => { const next = { ...prev }; delete next[category.id]; return next })
                                    }}
                                    className="px-4 py-2 text-sm bg-surface-tertiary text-gray-300 rounded-xl hover:bg-border-light transition-colors"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}

                        {/* ── Licence requirements checklist panel ── */}
                        {expandedRequirementsId === category.id && (
                          <tr className="border-b border-border-light bg-surface-tertiary/30">
                            {/* FIX: was colSpan={13} — see note on the insurance row
                                editor panel above. */}
                            <td colSpan={99} className="px-5 py-4">
                              <div className="bg-surface-card border border-border-light rounded-xl p-4">
                                <p className="text-xs font-semibold text-gray-300 mb-1">Licence Requirements Checklist</p>
                                <p className="text-xs text-gray-500 mb-3">
                                  Documents an expert must provide for this category. Setting "Proves Insurance" on a
                                  requirement is what lets a verified document against it waive the matching insurance
                                  fee row above — a requirement not marked here can never trigger a waiver, no matter
                                  what an admin picks when reviewing the document itself.
                                </p>

                                {requirementError && (
                                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2 mb-3">
                                    <p className="text-red-400 text-xs">{requirementError}</p>
                                  </div>
                                )}

                                {/* A fee row with no requirement proving the same insurance type can
                                    never be waived: the tasker has nowhere to upload proof for it. */}
                                {!requirementsLoading[category.id] && !category.insuranceFeeExempt && (category.insuranceFeeRows || [])
                                  .filter((row) => !(requirementsByCategory[category.id] || []).some((r) => r.insuranceType === row.insuranceType))
                                  .map((row) => (
                                    <div key={`nofix-${row.insuranceType}`} className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-2 mb-3">
                                      <p className="text-yellow-400 text-xs">
                                        ⚠ There is an insurance fee for {INSURANCE_TYPE_LABELS[row.insuranceType as InsuranceType] || row.insuranceType} but
                                        no requirement below proves it, so taskers cannot upload proof and the fee can never be waived.
                                        Add a requirement and set "Proves Insurance" to {INSURANCE_TYPE_LABELS[row.insuranceType as InsuranceType] || row.insuranceType}.
                                      </p>
                                    </div>
                                  ))}

                                {requirementsLoading[category.id] ? (
                                  <p className="text-xs text-gray-500">Loading…</p>
                                ) : (requirementsByCategory[category.id] || []).length === 0 ? (
                                  <p className="text-xs text-gray-500 mb-3">No licence requirements defined for this category yet.</p>
                                ) : (
                                  <div className="space-y-2 mb-3">
                                    {(requirementsByCategory[category.id] || []).map((req) => (
                                      <div key={req.id} className="bg-surface-tertiary/40 border border-border-light rounded-lg p-3">
                                        {editingRequirementId === req.id && editRequirementDraft ? (
                                          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                                            <div>
                                              <label className="block text-xs text-gray-500 mb-1">Doc Type</label>
                                              <select
                                                value={editRequirementDraft.docType}
                                                onChange={(e) => setEditRequirementDraft((prev) => prev && ({ ...prev, docType: e.target.value as LicenseDocType }))}
                                                className="w-full bg-surface-card border border-border-light rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none"
                                              >
                                                {LICENSE_DOC_TYPES.map((t) => (
                                                  <option key={t} value={t}>{LICENSE_DOC_TYPE_LABELS[t]}</option>
                                                ))}
                                              </select>
                                            </div>
                                            <div>
                                              <label className="block text-xs text-gray-500 mb-1">Label</label>
                                              <input
                                                type="text"
                                                value={editRequirementDraft.label}
                                                onChange={(e) => setEditRequirementDraft((prev) => prev && ({ ...prev, label: e.target.value }))}
                                                className="w-full bg-surface-card border border-border-light rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none"
                                              />
                                            </div>
                                            <div>
                                              <label className="block text-xs text-gray-500 mb-1">
                                                Proves Insurance{editRequirementDraft.docType === 'insurance_proof' && <span className="text-red-400"> *</span>}
                                              </label>
                                              <select
                                                value={editRequirementDraft.insuranceType}
                                                onChange={(e) => setEditRequirementDraft((prev) => prev && ({ ...prev, insuranceType: e.target.value as InsuranceType | '' }))}
                                                className={`w-full bg-surface-card border rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none ${editRequirementDraft.docType === 'insurance_proof' && !editRequirementDraft.insuranceType ? 'border-red-500' : 'border-border-light'}`}
                                              >
                                                <option value="">{editRequirementDraft.docType === 'insurance_proof' ? '— Choose one (required) —' : '— Not insurance —'}</option>
                                                {(Object.keys(INSURANCE_TYPE_LABELS) as InsuranceType[]).map((t) => (
                                                  <option key={t} value={t}>{INSURANCE_TYPE_LABELS[t]}</option>
                                                ))}
                                              </select>
                                            </div>
                                            <div>
                                              <label className="block text-xs text-gray-500 mb-1" title="Who must hold/upload this document. 'Company' means a roster worker never has to upload their own copy — see the hint below.">
                                                Held By
                                              </label>
                                              <select
                                                value={editRequirementDraft.verificationLevel}
                                                onChange={(e) => setEditRequirementDraft((prev) => prev && ({ ...prev, verificationLevel: e.target.value as LicenseVerificationLevel }))}
                                                className="w-full bg-surface-card border border-border-light rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none"
                                              >
                                                {(Object.keys(VERIFICATION_LEVEL_LABELS) as LicenseVerificationLevel[]).map((v) => (
                                                  <option key={v} value={v}>{VERIFICATION_LEVEL_LABELS[v]}</option>
                                                ))}
                                              </select>
                                            </div>
                                            <div>
                                              <label className="block text-xs text-gray-500 mb-1" title="Individuals may skip an Optional insurance requirement (uploading it removes the insurance fee). Companies always have to upload it.">
                                                Individuals
                                              </label>
                                              <select
                                                value={editRequirementDraft.isOptional && editRequirementDraft.insuranceType && editRequirementDraft.verificationLevel === 'individual' ? 'optional' : 'mandatory'}
                                                disabled={!editRequirementDraft.insuranceType || editRequirementDraft.verificationLevel !== 'individual'}
                                                onChange={(e) => setEditRequirementDraft((prev) => prev && ({ ...prev, isOptional: e.target.value === 'optional' }))}
                                                className="w-full bg-surface-card border border-border-light rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none disabled:opacity-50"
                                              >
                                                <option value="mandatory">Mandatory</option>
                                                <option value="optional">Optional</option>
                                              </select>
                                            </div>
                                            <div>
                                              <label className="block text-xs text-gray-500 mb-1">Description</label>
                                              <input
                                                type="text"
                                                value={editRequirementDraft.description}
                                                onChange={(e) => setEditRequirementDraft((prev) => prev && ({ ...prev, description: e.target.value }))}
                                                className="w-full bg-surface-card border border-border-light rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none"
                                              />
                                            </div>
                                            <div className="col-span-2 sm:col-span-5 flex items-center gap-2 mt-1">
                                              <button
                                                onClick={() => handleUpdateRequirement(category.id, req.id)}
                                                disabled={requirementSavingId === req.id}
                                                className="px-3 py-1.5 text-xs bg-brand-600 text-white rounded-lg hover:bg-brand-500 disabled:opacity-50"
                                              >
                                                {requirementSavingId === req.id ? 'Saving…' : 'Save'}
                                              </button>
                                              <button
                                                onClick={() => { setEditingRequirementId(null); setEditRequirementDraft(null) }}
                                                className="px-3 py-1.5 text-xs bg-surface-tertiary text-gray-300 rounded-lg hover:bg-border-light"
                                              >
                                                Cancel
                                              </button>
                                            </div>
                                          </div>
                                        ) : (
                                          <div className="flex items-center justify-between">
                                            <div>
                                              <p className="text-xs font-medium text-white">
                                                {req.label}
                                                {req.insuranceType && (
                                                  <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-blue-600/20 text-blue-400">
                                                    Proves: {INSURANCE_TYPE_LABELS[req.insuranceType]}
                                                  </span>
                                                )}
                                                {req.isOptional && (
                                                  <span
                                                    className="ml-2 px-2 py-0.5 text-xs rounded-full bg-green-600/20 text-green-400"
                                                    title="Individuals may skip this. Uploading an accepted document removes the insurance fee. Companies must always upload it."
                                                  >
                                                    Optional for individuals
                                                  </span>
                                                )}
                                                {isMandatoryIndividualInsuranceLine(req) && (
                                                  <span
                                                    className="ml-2 px-2 py-0.5 text-xs rounded-full bg-amber-600/20 text-amber-400"
                                                    title="Individuals must upload this before they can finish onboarding in this category. Normally insurance is optional for individuals — Edit → Individuals: Optional to change it back."
                                                  >
                                                    ⚠️ Mandatory for individuals
                                                  </span>
                                                )}
                                                {req.insuranceType && (
                                                  <span
                                                    className="ml-2 px-2 py-0.5 text-xs rounded-full bg-indigo-600/20 text-indigo-300"
                                                    title="Companies and Company applicants must always upload this during onboarding, for admin review. Never waived."
                                                  >
                                                    🏢 Required for companies
                                                  </span>
                                                )}
                                                {req.verificationLevel === 'company' && (
                                                  <span
                                                    className="ml-2 px-2 py-0.5 text-xs rounded-full bg-purple-600/20 text-purple-400"
                                                    title="Satisfied by the company's own verified document — roster workers are never asked to upload their own copy."
                                                  >
                                                    🏢 Company-held
                                                  </span>
                                                )}
                                              </p>
                                              <p className="text-xs text-gray-500 mt-0.5">
                                                {LICENSE_DOC_TYPE_LABELS[req.docType]}
                                                {req.description ? ` — ${req.description}` : ''}
                                              </p>
                                            </div>
                                            {canModify && (
                                              <div className="flex items-center gap-2 shrink-0 ml-3">
                                                <button
                                                  onClick={() => startEditRequirement(req)}
                                                  className="text-xs text-brand-400 hover:text-brand-300"
                                                >
                                                  Edit
                                                </button>
                                                <button
                                                  onClick={() => handleDeleteRequirement(category.id, req.id, req.label)}
                                                  disabled={requirementSavingId === req.id}
                                                  className="text-xs text-red-400 hover:text-red-300 disabled:opacity-40"
                                                >
                                                  ✕
                                                </button>
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {canModify && (
                                  newRequirementDraft[category.id] ? (
                                    <div className="border-t border-border-light pt-3">
                                      <p className="text-xs font-semibold text-gray-300 mb-2">New Requirement</p>
                                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                                        <div>
                                          <label className="block text-xs text-gray-500 mb-1">Doc Type</label>
                                          <select
                                            value={newRequirementDraft[category.id].docType}
                                            onChange={(e) => setNewRequirementDraft((prev) => ({
                                              ...prev, [category.id]: { ...prev[category.id], docType: e.target.value as LicenseDocType },
                                            }))}
                                            className="w-full bg-surface-tertiary border border-border-light rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none"
                                          >
                                            {LICENSE_DOC_TYPES.map((t) => (
                                              <option key={t} value={t}>{LICENSE_DOC_TYPE_LABELS[t]}</option>
                                            ))}
                                          </select>
                                        </div>
                                        <div>
                                          <label className="block text-xs text-gray-500 mb-1">Label</label>
                                          <input
                                            type="text"
                                            placeholder='e.g. "Liability Insurance (min $2,000,000)"'
                                            value={newRequirementDraft[category.id].label}
                                            onChange={(e) => setNewRequirementDraft((prev) => ({
                                              ...prev, [category.id]: { ...prev[category.id], label: e.target.value },
                                            }))}
                                            className="w-full bg-surface-tertiary border border-border-light rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none"
                                          />
                                        </div>
                                        <div>
                                          <label className="block text-xs text-gray-500 mb-1">
                                            Proves Insurance{newRequirementDraft[category.id].docType === 'insurance_proof' && <span className="text-red-400"> *</span>}
                                          </label>
                                          <select
                                            value={newRequirementDraft[category.id].insuranceType}
                                            onChange={(e) => setNewRequirementDraft((prev) => ({
                                              ...prev, [category.id]: { ...prev[category.id], insuranceType: e.target.value as InsuranceType | '' },
                                            }))}
                                            className={`w-full bg-surface-tertiary border rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none ${newRequirementDraft[category.id].docType === 'insurance_proof' && !newRequirementDraft[category.id].insuranceType ? 'border-red-500' : 'border-border-light'}`}
                                          >
                                            <option value="">{newRequirementDraft[category.id].docType === 'insurance_proof' ? '— Choose one (required) —' : '— Not insurance —'}</option>
                                            {(Object.keys(INSURANCE_TYPE_LABELS) as InsuranceType[]).map((t) => (
                                              <option key={t} value={t}>{INSURANCE_TYPE_LABELS[t]}</option>
                                            ))}
                                          </select>
                                        </div>
                                        <div>
                                          <label className="block text-xs text-gray-500 mb-1" title="Who must hold/upload this document. 'Company' means a roster worker never has to upload their own copy.">
                                            Held By
                                          </label>
                                          <select
                                            value={newRequirementDraft[category.id].verificationLevel}
                                            onChange={(e) => setNewRequirementDraft((prev) => ({
                                              ...prev, [category.id]: { ...prev[category.id], verificationLevel: e.target.value as LicenseVerificationLevel },
                                            }))}
                                            className="w-full bg-surface-tertiary border border-border-light rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none"
                                          >
                                            {(Object.keys(VERIFICATION_LEVEL_LABELS) as LicenseVerificationLevel[]).map((v) => (
                                              <option key={v} value={v}>{VERIFICATION_LEVEL_LABELS[v]}</option>
                                            ))}
                                          </select>
                                        </div>
                                        <div>
                                          <label className="block text-xs text-gray-500 mb-1" title="Individuals may skip an Optional insurance requirement (uploading it removes the insurance fee). Companies always have to upload it.">
                                            Individuals
                                          </label>
                                          <select
                                            value={newRequirementDraft[category.id].isOptional && newRequirementDraft[category.id].insuranceType && newRequirementDraft[category.id].verificationLevel === 'individual' ? 'optional' : 'mandatory'}
                                            disabled={!newRequirementDraft[category.id].insuranceType || newRequirementDraft[category.id].verificationLevel !== 'individual'}
                                            onChange={(e) => setNewRequirementDraft((prev) => ({
                                              ...prev, [category.id]: { ...prev[category.id], isOptional: e.target.value === 'optional' },
                                            }))}
                                            className="w-full bg-surface-tertiary border border-border-light rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none disabled:opacity-50"
                                          >
                                            <option value="mandatory">Mandatory</option>
                                            <option value="optional">Optional</option>
                                          </select>
                                        </div>
                                        <div>
                                          <label className="block text-xs text-gray-500 mb-1">Description (optional)</label>
                                          <input
                                            type="text"
                                            value={newRequirementDraft[category.id].description}
                                            onChange={(e) => setNewRequirementDraft((prev) => ({
                                              ...prev, [category.id]: { ...prev[category.id], description: e.target.value },
                                            }))}
                                            className="w-full bg-surface-tertiary border border-border-light rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none"
                                          />
                                        </div>
                                      </div>
                                      <p className="text-xs text-gray-500 mt-2 mb-3">
                                        A category can have at most one requirement per insurance type — creating a
                                        second one for the same type is rejected server-side. "Held By: Company"
                                        means a roster worker satisfies this requirement through their company's own
                                        verified document instead of uploading their own copy.
                                      </p>
                                      <div className="flex items-center gap-2">
                                        <button
                                          onClick={() => handleCreateRequirement(category.id)}
                                          disabled={requirementSavingId === category.id}
                                          className="px-4 py-2 text-sm bg-brand-600 text-white rounded-xl hover:bg-brand-500 disabled:opacity-50 transition-colors"
                                        >
                                          {requirementSavingId === category.id ? 'Saving…' : 'Add Requirement'}
                                        </button>
                                        <button
                                          onClick={() => setNewRequirementDraft((prev) => { const next = { ...prev }; delete next[category.id]; return next })}
                                          className="px-4 py-2 text-sm bg-surface-tertiary text-gray-300 rounded-xl hover:bg-border-light transition-colors"
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => initNewRequirementDraft(category.id)}
                                      className="text-xs text-brand-400 hover:text-brand-300"
                                    >
                                      + Add Requirement
                                    </button>
                                  )
                                )}
                              </div>
                            </td>
                          </tr>
                        )}

                        {/* ── Task Reach + Availability Count + Escalation ─── */}
                        {expandedReachId === category.id && (
                          <tr>
                            <td colSpan={99} className="px-5 pb-5 pt-0">
                              <div className="bg-surface-tertiary border border-border-light rounded-xl p-4 mt-2">
                                <p className="text-xs font-semibold text-gray-300 uppercase tracking-wider mb-4">
                                  🗺️ Task Reach &amp; Availability
                                </p>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                  {/* Task Reach */}
                                  <div>
                                    <label className="text-xs text-gray-400 mb-1 block">Task Reach</label>
                                    <p className="text-xs text-gray-500 mb-2">Controls which taskers receive push notifications when a task posts. Does not affect board visibility.</p>
                                    <select
                                      value={category.taskReach ?? 'radius'}
                                      onChange={async (e) => {
                                        const taskReach = e.target.value as 'local' | 'radius' | 'global'
                                        const categoryId = category.id
                                        const prevTaskReach = category.taskReach
                                        const prevRadius    = category.maxTaskRadiusKm
                                        // Radius needs a distance — the default (100 km, 30 km for pickup-code
                                        // food categories) if none yet; local / global have none.
                                        const maxTaskRadiusKm = taskReach === 'radius' ? (category.maxTaskRadiusKm ?? defaultRadiusFor(category)) : null
                                        setCategories((prev) => prev.map((c) => c.id === categoryId ? { ...c, taskReach, maxTaskRadiusKm } : c))
                                        try { await updateCategoryReach(categoryId, { taskReach, maxTaskRadiusKm }) }
                                        catch (err) {
                                          setCategories((prev) => prev.map((c) => c.id === categoryId ? { ...c, taskReach: prevTaskReach, maxTaskRadiusKm: prevRadius } : c))
                                          reachSaveError(err)
                                        }
                                      }}
                                      className="w-full bg-surface-secondary border border-border-light rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-500"
                                    >
                                      <option value="local">📍 Local — same city as customer</option>
                                      <option value="radius">🔵 Radius — within X km</option>
                                      <option value="global">🌐 Global — anywhere</option>
                                    </select>
                                  </div>

                                  {/* Max Radius */}
                                  <div>
                                    <label className="text-xs text-gray-400 mb-1 block">Max Radius (km)</label>
                                    <p className="text-xs text-gray-500 mb-2">Only used when Task Reach = Radius. Default {DEFAULT_RADIUS_KM} km ({DEFAULT_FOOD_RADIUS_KM} km for Food Delivery &amp; Meal Services). Step 2 escalation expands to 2× this value.</p>
                                    <input
                                      type="number"
                                      min={1}
                                      placeholder={`e.g. ${defaultRadiusFor(category)}`}
                                      key={`${category.id}-radius-${category.maxTaskRadiusKm ?? ''}`}
                                      defaultValue={category.maxTaskRadiusKm ?? ''}
                                      disabled={category.taskReach !== 'radius'}
                                      // Saved when the admin leaves the field — not on every keystroke, which
                                      // used to send half-typed values ("1" while typing "15") and empty ones.
                                      onBlur={async (e) => {
                                        const raw = e.target.value.trim()
                                        const categoryId = category.id
                                        const prevVal = category.maxTaskRadiusKm ?? null
                                        const val = raw === '' ? null : Number(raw)
                                        if (val === prevVal) return
                                        if (val === null || !Number.isInteger(val) || val < 1) {
                                          window.alert('Enter the radius as a whole number of km (1 or more).')
                                          e.target.value = prevVal === null ? '' : String(prevVal)
                                          return
                                        }
                                        setCategories((prev) => prev.map((c) => c.id === categoryId ? { ...c, maxTaskRadiusKm: val } : c))
                                        try { await updateCategoryReach(categoryId, { maxTaskRadiusKm: val }) }
                                        catch (err) {
                                          setCategories((prev) => prev.map((c) => c.id === categoryId ? { ...c, maxTaskRadiusKm: prevVal } : c))
                                          reachSaveError(err)
                                        }
                                      }}
                                      className="w-full bg-surface-secondary border border-border-light rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-500 disabled:opacity-40"
                                    />
                                  </div>

                                  {/* Availability Count Display */}
                                  <div>
                                    <label className="text-xs text-gray-400 mb-1 block">Availability Count Display</label>
                                    <p className="text-xs text-gray-500 mb-2">What count the customer sees after picking this category in CreateTaskScreen.</p>
                                    <select
                                      value={category.showAvailabilityCount ?? 'none'}
                                      onChange={async (e) => {
                                        const showAvailabilityCount = e.target.value as 'none' | 'total' | 'online'
                                        const categoryId = category.id
                                        const prevVal = category.showAvailabilityCount
                                        setCategories((prev) => prev.map((c) => c.id === categoryId ? { ...c, showAvailabilityCount } : c))
                                        try { await updateCategoryReach(categoryId, { showAvailabilityCount }) }
                                        catch (err) {
                                          setCategories((prev) => prev.map((c) => c.id === categoryId ? { ...c, showAvailabilityCount: prevVal } : c))
                                          reachSaveError(err)
                                        }
                                      }}
                                      className="w-full bg-surface-secondary border border-border-light rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-500"
                                    >
                                      <option value="none">None — show nothing</option>
                                      <option value="total">Total — "12 taskers near you"</option>
                                      <option value="online">Online — "3 online now" (food delivery)</option>
                                    </select>
                                  </div>

                                  {/* Allow Customer to Expand Reach */}
                                  <div>
                                    <label className="text-xs text-gray-400 mb-1 block">Allow Customer to Expand Reach</label>
                                    <p className="text-xs text-gray-500 mb-2">Shows "📍 Search nearby / 🌐 Search anywhere" toggle to customer. Not for food delivery.</p>
                                    <label className={`flex items-center gap-2 mt-3 ${category.escalationEnabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}>
                                      <input
                                        type="checkbox"
                                        disabled={category.escalationEnabled ?? false}
                                        checked={category.allowCustomerToExpandReach ?? false}
                                        onChange={async (e) => {
                                          const allowCustomerToExpandReach = e.target.checked
                                          const categoryId = category.id
                                          const prevVal = category.allowCustomerToExpandReach
                                          // Enforce: food delivery (escalation enabled) cannot allow reach expansion
                                          if (allowCustomerToExpandReach && category.escalationEnabled) return
                                          setCategories((prev) => prev.map((c) => c.id === categoryId ? { ...c, allowCustomerToExpandReach } : c))
                                          try { await updateCategoryReach(categoryId, { allowCustomerToExpandReach }) }
                                          catch (err) {
                                            setCategories((prev) => prev.map((c) => c.id === categoryId ? { ...c, allowCustomerToExpandReach: prevVal } : c))
                                            reachSaveError(err)
                                          }
                                        }}
                                        className="w-4 h-4"
                                      />
                                      <span className="text-sm text-gray-300">{category.allowCustomerToExpandReach ? 'Enabled' : 'Disabled'}</span>
                                    </label>
                                  </div>
                                </div>

                                {/* Escalation — food delivery */}
                                <div className="border-t border-border-light pt-4 mt-2">
                                  <div className="flex items-center gap-3 mb-3">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={category.escalationEnabled ?? false}
                                        onChange={async (e) => {
                                          const escalationEnabled = e.target.checked
                                          const categoryId = category.id
                                          const prevEscalation = category.escalationEnabled
                                          const prevExpand = category.allowCustomerToExpandReach
                                          const prevSteps = {
                                            escalationStep1Minutes: category.escalationStep1Minutes,
                                            escalationStep2Minutes: category.escalationStep2Minutes,
                                            escalationStep3Minutes: category.escalationStep3Minutes,
                                          }
                                          // Enforce: enabling escalation forces allowCustomerToExpandReach off and
                                          // fills any empty step time with its default (2 / 4 / 30 minutes).
                                          const updates = escalationEnabled
                                            ? {
                                                escalationEnabled,
                                                allowCustomerToExpandReach: false,
                                                escalationStep1Minutes: category.escalationStep1Minutes ?? 2,
                                                escalationStep2Minutes: category.escalationStep2Minutes ?? 4,
                                                escalationStep3Minutes: category.escalationStep3Minutes ?? 30,
                                              }
                                            : { escalationEnabled }
                                          setCategories((prev) => prev.map((c) => c.id === categoryId ? { ...c, ...updates } : c))
                                          try { await updateCategoryReach(categoryId, updates) }
                                          catch (err) {
                                            setCategories((prev) => prev.map((c) => c.id === categoryId ? { ...c, escalationEnabled: prevEscalation, allowCustomerToExpandReach: prevExpand, ...prevSteps } : c))
                                            reachSaveError(err)
                                          }
                                        }}
                                        className="w-4 h-4"
                                      />
                                      <span className="text-sm font-medium text-gray-300">🍔 Enable Escalation (food delivery)</span>
                                    </label>
                                  </div>

                                  {category.escalationEnabled && (
                                    <div className="grid grid-cols-3 gap-3 ml-6">
                                      {[
                                        { field: 'escalationStep1Minutes' as const, label: 'Step 1 (min)', hint: 'Notify all in city. Default: 2' },
                                        { field: 'escalationStep2Minutes' as const, label: 'Step 2 (min)', hint: 'Expand radius + warn customer. Default: 4' },
                                        { field: 'escalationStep3Minutes' as const, label: 'Step 3 (min)', hint: 'Stop all notifications. Default: 30' },
                                      ].map(({ field, label, hint }) => (
                                        <div key={field}>
                                          <label className="text-xs text-gray-400 mb-1 block">{label}</label>
                                          <p className="text-xs text-gray-500 mb-1">{hint}</p>
                                          <input
                                            type="number"
                                            min={1}
                                            key={`${category.id}-${field}-${category[field] ?? ''}`}
                                            defaultValue={category[field] ?? ''}
                                            // Saved when the admin leaves the field (not on every keystroke).
                                            onBlur={async (e) => {
                                              const raw = e.target.value.trim()
                                              const prevVal = category[field] ?? null
                                              const val = raw === '' ? null : Number(raw)
                                              if (val === prevVal) return
                                              if (val === null || !Number.isInteger(val) || val < 1) {
                                                window.alert('Enter the time as a whole number of minutes (1 or more).')
                                                e.target.value = prevVal === null ? '' : String(prevVal)
                                                return
                                              }
                                              setCategories((prev) => prev.map((c) => c.id === category.id ? { ...c, [field]: val } : c))
                                              try { await updateCategoryReach(category.id, { [field]: val }) }
                                              catch (err) {
                                                setCategories((prev) => prev.map((c) => c.id === category.id ? { ...c, [field]: prevVal } : c))
                                                reachSaveError(err)
                                              }
                                            }}
                                            className="w-full bg-surface-secondary border border-border-light rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-500"
                                          />
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>

                                {/* ── Vehicle requirement ─────────────────────────── */}
                                <div className="mt-4 pt-4 border-t border-border-light">
                                  <label className="text-xs text-gray-400 mb-1 block">Vehicle requirement</label>
                                  <p className="text-xs text-gray-500 mb-2">
                                    Off: every expert in this category is shown. On: customers only see experts — and the
                                    "taskers available" number only counts experts — with an approved vehicle of the chosen kind.
                                    {!category.isTwoLocation && ' Only available for two-location categories: experts register vehicles only for those.'}
                                    {' '}A category with a requirement also needs commercial auto insurance from companies.
                                  </p>
                                  <select
                                    value={category.vehicleRequirement ?? 'any'}
                                    disabled={!category.isTwoLocation && (category.vehicleRequirement ?? 'any') === 'any'}
                                    onChange={async (e) => {
                                      const vehicleRequirement = e.target.value as 'any' | 'motorized' | 'car_van' | 'van_truck'
                                      const categoryId = category.id
                                      const prevValue  = category.vehicleRequirement
                                      setCategories((prev) => prev.map((c) => c.id === categoryId ? { ...c, vehicleRequirement } : c))
                                      try { await updateCategory(categoryId, { vehicleRequirement }) }
                                      catch (err) {
                                        setCategories((prev) => prev.map((c) => c.id === categoryId ? { ...c, vehicleRequirement: prevValue } : c))
                                        reachSaveError(err)
                                      }
                                    }}
                                    className="w-full bg-surface-secondary border border-border-light rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-500"
                                  >
                                    <option value="any">Off — no vehicle required</option>
                                    <option value="motorized">Motorized — car, van, truck, moped or motorcycle</option>
                                    <option value="car_van">Car or van</option>
                                    <option value="van_truck">Van or truck</option>
                                  </select>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )
        })
      )}
    </div>
  )
}

export default CategoriesPage